/* ------------------------------------------------------------------------- *
 * TFC Dash — Merchant onboarding intake (Terraform Capital, powered by Idenza)
 *
 * Standalone page, declared directly in App.js at /onboarding-intake — not
 * part of Navigator's realm-prefixed routing or sidebar. It carries no
 * ?realm= query param and shows no Navigator chrome. The ONLY thing it needs
 * from the rest of the app is Keycloak login: on mount it silently checks for
 * an existing SSO session (same initKeycloak() used by Navigator) and, if
 * there isn't one, redirects to Keycloak's hosted login page and back. There
 * is no per-tenant realm resolution here beyond whatever keycloak.js already
 * defaults to (the "Idenza" realm, unless a realm happens to be in the URL or
 * session already) — this page does not read or care about realm beyond that.
 *
 * Single file by design: this used to be a 14-file directory
 * (TfcDashOnboarding/{OnboardingPage,Stepper,UploadStep,ConfirmReadingStep,
 * ScreeningStep,ReportStep,NotesPanel,useScreeningRunner,api,payloads,status,
 * checklist,shared,ui}) and is now one file, consolidated verbatim (no
 * behavior changes) to match the single-file convention every other
 * Navigator-launched page in this app already uses (e.g.
 * SSBAmlExecutionReport.jsx, SSBComplianceRemediationAgent.jsx).
 *
 *   import TfcDashOnboardingPage from "./components/TfcDashOnboardingPage";
 *   <Route path="/onboarding-intake" element={<TfcDashOnboardingPage />} />
 * ------------------------------------------------------------------------- */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { Button, Card } from "../design/primitives";
import { color, radius, shadow, space, text, weight } from "../design/tokens";
import { getKeycloakInstance, initKeycloak, setCurrentRealm } from "../keycloak";

// This page only ever talks to the "tfcdas" realm's backend (adapters/tfcdas.py
// in onboarding_services, and TFC_KEYCLOAK_REALMS on rules-orchestrator-py).
// getCurrentRealm()'s own resolution (URL ?realm= -> sessionStorage -> the
// app-wide "Idenza" default) has nothing tfcdas-specific about it, so without
// pinning it here, a browser that already has ambient session state for some
// other realm (e.g. from using Navigator earlier the same day) would silently
// authenticate into THAT realm instead -- Keycloak logs the request through
// fine, and only the backend's realm allow-list catches it, as
// "This account can't use onboarding."
const TFC_REALM = "tfcdas";

/* ========================================================================
 * Keycloak auth gate — same pattern Navigator.js uses at its own top level
 * (initKeycloak() → check-sso; not authenticated and no auth params on the
 * URL → keycloak.login() to force a real redirect), minus realm-prefixed
 * links, the sidebar, and tenant/realm display — this page doesn't need any
 * of that, only "is someone logged in".
 * ========================================================================
 */
function hasAuthParamsInUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") || params.has("session_state");
  } catch {
    return false;
  }
}

// Local logout, not the shared keycloak.js one: that redirects to "/login",
// the Navigator org/realm picker, which has no notion of the "tfcdas" realm
// this page uses. Redirecting back here instead re-triggers AuthGate's own
// keycloak.login() against the right realm.
function tfcLogout() {
  const kc = getKeycloakInstance();
  try {
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("tenant_id");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("active_realm");
    Cookies.remove("access_token", { path: "/" });
  } catch {
    // best-effort cleanup; kc.logout() below ends the real session either way
  }
  const redirectUri = window.location.origin + "/onboarding-intake";
  if (kc && typeof kc.logout === "function") {
    kc.logout({ redirectUri });
  } else {
    window.location.href = redirectUri;
  }
}

function AuthGate({ children }) {
  const [state, setState] = useState("checking"); // "checking" | "authenticated" | "redirecting" | "error"
  const [errorMessage, setErrorMessage] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let cancelled = false;

    (async () => {
      try {
        setCurrentRealm(TFC_REALM);
        const keycloak = await initKeycloak();
        if (cancelled) return;
        if (keycloak?.authenticated && keycloak.token) {
          setState("authenticated");
          return;
        }
        // Not authenticated: if we're mid-redirect-back from Keycloak (auth
        // params still on the URL), don't loop — just wait for the next tick.
        if (hasAuthParamsInUrl()) {
          setState("checking");
          return;
        }
        setState("redirecting");
        await keycloak.login({ redirectUri: window.location.href });
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[TfcDashOnboardingPage] Keycloak init failed:", err);
        setErrorMessage(err?.message || "Could not reach the login service.");
        setState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "authenticated") return children;

  if (state === "error") {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: space[5], textAlign: "center" }}>
        <div style={{ fontSize: text.lg, fontWeight: weight.semibold, color: color.text.primary, marginBottom: space[2] }}>
          Couldn't sign you in
        </div>
        <div style={{ fontSize: text.base, color: color.text.tertiary, marginBottom: space[4] }}>{errorMessage}</div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: "8px 20px", borderRadius: radius.md, border: `1px solid ${color.border.strong}`,
            background: color.bg.surface, color: color.text.primary, cursor: "pointer", fontFamily: "inherit", fontSize: text.base,
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  // "checking" / "redirecting" — same minimal loading state either way, so
  // there's no flash of content before the redirect actually happens.
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: space[3] }}>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block", width: 20, height: 20, borderRadius: "50%",
          border: `2px solid ${color.border.subtle}`, borderTopColor: color.brand.solid,
          animation: "tfc-onb-spin 0.8s linear infinite",
        }}
      />
      <style>{"@keyframes tfc-onb-spin { to { transform: rotate(360deg); } }"}</style>
      <span style={{ fontSize: text.base, color: color.text.tertiary }}>Signing you in…</span>
    </div>
  );
}

/* ========================================================================
 * UI primitives (formerly ui.jsx) — tones, spinner, badges, notices, a
 * stage-progress list and an error boundary. Colours are defined here, not
 * taken from the design-system Chip, so every state has one consistent
 * colour: success = green, warning = amber, danger = red, info/in-progress =
 * blue, neutral = grey.
 * ========================================================================
 */
const TONES = {
  success: { fg: "#067647", bg: "#ECFDF3", border: "#ABEFC6", solid: "#17B26A" },
  warning: { fg: "#B54708", bg: "#FFFAEB", border: "#FEDF89", solid: "#F79009" },
  danger: { fg: "#B42318", bg: "#FEF3F2", border: "#FECDCA", solid: "#F04438" },
  info: { fg: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF", solid: "#2E90FA" },
  neutral: { fg: "#475467", bg: "#F2F4F7", border: "#E4E7EC", solid: "#98A2B3" },
};
const tone = (t) => TONES[t] || TONES.neutral;

const STYLE_ID = "onb-ui-keyframes";
const CSS = `
@keyframes onb-spin { to { transform: rotate(360deg); } }
@keyframes onb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
@keyframes onb-slide { 0% { left: -40%; } 100% { left: 100%; } }
@keyframes onb-ring { 0% { box-shadow: 0 0 0 0 rgba(46,144,250,.45); } 100% { box-shadow: 0 0 0 7px rgba(46,144,250,0); } }
@media (prefers-reduced-motion: reduce) {
  .onb-anim { animation: none !important; }
}`;
function useKeyframes() {
  useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

function Spinner({ size = 14, tone: t = "info", label }) {
  useKeyframes();
  const c = tone(t);
  return (
    <span
      role={label ? "status" : undefined}
      aria-label={label}
      className="onb-anim"
      style={{
        display: "inline-block", width: size, height: size, flexShrink: 0, borderRadius: "50%",
        border: `${Math.max(2, Math.round(size / 7))}px solid ${c.border}`, borderTopColor: c.solid,
        animation: "onb-spin 0.8s linear infinite", verticalAlign: "middle",
      }}
    />
  );
}

const ICONS = { success: "✓", danger: "✕", warning: "!", info: "i", neutral: "–" };

function StatusBadge({ tone: t = "neutral", busy, size = "sm", title, children }) {
  useKeyframes();
  const c = tone(t);
  const big = size === "md";
  return (
    <span
      title={title}
      data-status={t}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        padding: big ? "4px 12px" : "2px 9px", borderRadius: 999, border: `1px solid ${c.border}`,
        background: c.bg, color: c.fg, fontSize: big ? text.base : text.sm, fontWeight: weight.semibold, lineHeight: 1.5,
      }}
    >
      {busy ? (
        <Spinner size={big ? 12 : 10} tone={t} />
      ) : (
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: c.solid, flexShrink: 0 }} />
      )}
      {children}
    </span>
  );
}

function IconDot({ tone: t, size = 18 }) {
  const c = tone(t);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size,
        borderRadius: "50%", background: c.solid, color: "#fff", fontSize: Math.round(size * 0.62), fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {ICONS[t] || ICONS.neutral}
    </span>
  );
}

function Notice({ tone: t = "info", title, children, actions, busy }) {
  const c = tone(t);
  return (
    <div
      role={t === "danger" || t === "warning" ? "alert" : "status"}
      data-tone={t}
      style={{
        display: "flex", gap: space[3], alignItems: "flex-start", padding: `${space[3]}px ${space[4]}px`,
        border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.solid}`, borderRadius: radius.lg, background: c.bg,
      }}
    >
      <span style={{ marginTop: 2, flexShrink: 0 }}>
        {busy ? <Spinner size={16} tone={t} /> : <IconDot tone={t} size={18} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ fontSize: text.md, fontWeight: weight.semibold, color: c.fg }}>{title}</div>}
        {children && (
          <div style={{ fontSize: text.base, color: color.text.secondary, marginTop: title ? 2 : 0, wordBreak: "break-word" }}>
            {children}
          </div>
        )}
        {actions && <div style={{ marginTop: space[2], display: "flex", gap: space[2], flexWrap: "wrap" }}>{actions}</div>}
      </div>
    </div>
  );
}

function IndeterminateBar({ tone: t = "info" }) {
  useKeyframes();
  const c = tone(t);
  return (
    <div role="progressbar" aria-busy="true" style={{ position: "relative", height: 4, overflow: "hidden", borderRadius: 2, background: c.bg }}>
      <div
        className="onb-anim"
        style={{
          position: "absolute", top: 0, bottom: 0, width: "40%", borderRadius: 2, background: c.solid,
          animation: "onb-slide 1.4s ease-in-out infinite",
        }}
      />
    </div>
  );
}

const STAGE_TONE = { done: "success", active: "info", pending: "neutral", error: "danger", warning: "warning", skipped: "neutral" };
function ProgressList({ stages }) {
  useKeyframes();
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {stages.map((s, i) => {
        const t = STAGE_TONE[s.state] || "neutral";
        const c = tone(t);
        const last = i === stages.length - 1;
        return (
          <li key={s.key || s.label} data-stage={s.state} style={{ display: "flex", gap: space[3], position: "relative" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 22, flexShrink: 0 }}>
              <StageIcon state={s.state} />
              {!last && (
                <span style={{ flex: 1, width: 2, minHeight: 14, background: s.state === "done" ? TONES.success.border : color.border.subtle }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : space[3] }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: space[3], flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: text.md, fontWeight: s.state === "active" ? weight.semibold : weight.medium,
                    color: s.state === "pending" || s.state === "skipped" ? color.text.tertiary : color.text.primary,
                  }}
                >
                  {s.label}
                </span>
                {s.status && <span style={{ fontSize: text.sm, fontWeight: weight.medium, color: c.fg }}>{s.status}</span>}
              </div>
              {s.detail && <div style={{ fontSize: text.sm, color: color.text.tertiary, marginTop: 1 }}>{s.detail}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function StageIcon({ state }) {
  const size = 22;
  if (state === "active") {
    return (
      <span
        className="onb-anim"
        style={{
          width: size, height: size, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: TONES.info.bg, animation: "onb-ring 1.4s ease-out infinite",
        }}
      >
        <Spinner size={14} tone="info" />
      </span>
    );
  }
  if (state === "pending" || state === "skipped") {
    return (
      <span
        aria-hidden="true"
        style={{
          width: size, height: size, borderRadius: "50%", border: `2px ${state === "skipped" ? "dashed" : "solid"} ${color.border.strong}`,
          background: color.bg.surface, boxSizing: "border-box",
        }}
      />
    );
  }
  return <IconDot tone={STAGE_TONE[state]} size={size} />;
}

function Elapsed({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!since) return null;
  const s = Math.max(0, Math.floor((now - since) / 1000));
  const m = Math.floor(s / 60);
  return <>{m ? `${m}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`}</>;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[onboarding] render error", error, info && info.componentStack);
  }

  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Notice
        tone="danger"
        title="This step couldn't be displayed"
        actions={
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            style={{
              padding: "4px 12px", borderRadius: 6, border: `1px solid ${TONES.danger.border}`, background: "#fff",
              color: TONES.danger.fg, cursor: "pointer", fontFamily: "inherit", fontSize: text.sm, fontWeight: weight.medium,
            }}
          >
            Try again
          </button>
        }
      >
        Something unexpected happened while showing this step. Your progress is kept — try again, or go to another
        step and come back.
      </Notice>
    );
  }
}

/* ========================================================================
 * Shared helpers (formerly shared.jsx) — field/address shaping, officer
 * merge logic, key-person rules, and shared form primitives.
 * ========================================================================
 */
const uid = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// merchant.incorporation.{country,state} is what the screening API actually
// routes KYB checks on — a distinct field from
// incorporation.registered_address.{country,state}, which is what the
// intake-form parser populates. There's
// no separate UI input for incorporation.country/state (removed per
// explicit direction — it was redundant with the registered address for
// virtually every real business), so derive it from the registered address
// at submission time instead of leaving it permanently empty. Confirmed
// live: leaving it empty makes the real screening backend fail closed with
// "State is REQUIRED for US but not provided".
//
// Spec (v2.1.0): incorporation.state is nullable (null for jurisdictions with
// no state), but every Address.state must be a STRING — "" not null — or the
// API returns 422.
function resolveIncorporation(merchant) {
  const addr = merchant.incorporation.registered_address || {};
  return {
    country: merchant.incorporation.country || addr.country || "",
    state: merchant.incorporation.state || addr.state || null,
    registered_address: cleanAddress(addr),
  };
}

// Address shape the spec requires: all five of line1/city/state/postal_code/
// country present as strings; line2 only when set.
function cleanAddress(addr) {
  const a = addr || {};
  const out = {
    line1: (a.line1 || "").trim(),
    city: (a.city || "").trim(),
    state: (a.state || "").trim(),
    postal_code: (a.postal_code || "").trim(),
    country: (a.country || "").trim().toUpperCase(),
  };
  if (a.line2 && a.line2.trim()) out.line2 = a.line2.trim();
  return out;
}

function addressIsComplete(addr) {
  const a = addr || {};
  // State may legitimately be "" outside the US (spec: send "" not null).
  const needsState = (a.country || "").toUpperCase() === "US";
  return !!(a.line1 && a.city && a.postal_code && a.country && (!needsState || a.state));
}

function addressIsEmpty(addr) {
  const a = addr || {};
  return !a.line1 && !a.city && !a.postal_code;
}

// document_type values accepted by /v1/verification/documents. Two-sided
// types require back_storage_uri at pre-validation.
// The API detects the document type from the image; document_type only says
// whether a back image is needed. National ID, state ID, REAL ID and generic
// ID cards all behave the same (two-sided), so they are one option here.
const ID_DOC_TYPES = [
  { value: "PASSPORT", label: "Passport", twoSided: false },
  { value: "DRIVERS_LICENSE", label: "Driver's licence", twoSided: true },
  { value: "ID_CARD", label: "National or state ID card", twoSided: true },
];

// Maps any value the API accepts (or the intake form parser returns) onto
// one of ID_DOC_TYPES.
const ID_DOC_TYPE_ALIASES = {
  PASSPORT_CARD: "PASSPORT",
  NATIONAL_ID: "ID_CARD", STATE_ID: "ID_CARD", REAL_ID: "ID_CARD",
  DRIVER_LICENSE: "DRIVERS_LICENSE", DL: "DRIVERS_LICENSE",
};
function canonicalIdDocType(value) {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return "";
  const mapped = ID_DOC_TYPE_ALIASES[v] || v;
  return ID_DOC_TYPES.some((t) => t.value === mapped) ? mapped : "ID_CARD";
}
const isTwoSided = (docType) => {
  const t = ID_DOC_TYPES.find((d) => d.value === docType);
  return t ? t.twoSided : true; // unknown types are treated as two-sided by the API
};

// File limits from the spec's "File Requirements" table.
const FILE_RULES = {
  id: { maxBytes: 10 * 1024 * 1024, accept: ".jpg,.jpeg,.png,.webp,.pdf", label: "JPEG, PNG, WEBP or PDF, max 10 MB" },
  selfie: { maxBytes: 10 * 1024 * 1024, accept: ".jpg,.jpeg,.png,.webp", label: "JPEG, PNG or WEBP, max 10 MB" },
  business: { maxBytes: 5 * 1024 * 1024, accept: ".pdf,.jpg,.jpeg,.png", label: "PDF, JPG or PNG, max 5 MB" },
  // The CDD intake form, read via onboarding_intake's prefill() (canonical keys).
  intake: { maxBytes: 25 * 1024 * 1024, accept: ".docx", label: "Word (.docx)" },
};

function checkFile(file, rule) {
  if (!file) return "No file selected.";
  if (file.size > rule.maxBytes) {
    return `${file.name} is ${(file.size / 1048576).toFixed(1)} MB — the limit is ${rule.maxBytes / 1048576} MB.`;
  }
  const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
  if (!rule.accept.split(",").includes(ext)) return `${file.name}: expected ${rule.label}.`;
  return null;
}

// Officer ids double as `subject_ref` on document verification, and appear in
// the report — short and readable beats a full UUID.
const newOfficerId = () => `off-${uid().replace(/-/g, "").slice(0, 8)}`;

function newOfficer(fields = {}) {
  return {
    officer_id: newOfficerId(),
    full_name: "",
    role: "",
    ownership_percentage: "",
    dob: "",
    nationality: "",
    country_of_residence: "",
    email: "",
    pep_declared: false,
    pep_detail: "",
    id_doc_type: "",
    documents: { id: null, id_back: null, selfie: null },
    ...fields,
  };
}

const normName = (name) => (name || "").trim().toLowerCase().replace(/\s+/g, " ");

// Merges the intake form's officers into the existing list by name. A match
// keeps its officer_id and documents (so an uploaded ID is never
// reattributed) and takes the form's values for empty fields. Unmatched form
// officers are appended.
function mergeExtractedOfficers(existingOfficers, extractedOfficers) {
  const officers = existingOfficers.map((o) => ({ ...o }));
  for (const ext of extractedOfficers || []) {
    const fields = {
      full_name: ext.full_name || "",
      role: ext.role || "",
      ownership_percentage: ext.ownership_percentage ?? "",
      dob: ext.dob || "",
      nationality: ext.nationality || "",
      country_of_residence: ext.country_of_residence || ext.nationality || "",
      email: ext.email || "",
      pep_declared: !!ext.pep_declared,
      pep_detail: ext.pep_detail || "",
      citizenship_raw: ext.citizenship_raw,
      nationalities: ext.nationalities || [],
      has_detail_block: !!ext.has_detail_block,
    };
    // Only people already on the page are matched; two people with the same
    // name on one form stay two people.
    const idx = officers.slice(0, existingOfficers.length)
      .findIndex((o) => normName(o.full_name) === normName(ext.full_name));
    if (idx >= 0) {
      const cur = officers[idx];
      const merged = { ...cur };
      for (const [k, v] of Object.entries(fields)) {
        if (cur[k] === undefined || cur[k] === "" || cur[k] === null) merged[k] = v;
      }
      officers[idx] = merged;
    } else {
      officers.push(newOfficer({ ...fields, id_doc_type: canonicalIdDocType(ext.id_document_type) }));
    }
  }
  return officers;
}

// Maps the parsed form's business fields onto the merchant record (a fresh
// record for each intake form, so every value comes from the form).
function mergeParsedMerchant(merchant, parsed) {
  const keep = (cur, next) => (cur && String(cur).trim() ? cur : next || cur);
  const business = parsed.business || {};
  const mergeAddr = (cur, next) =>
    addressIsEmpty(cur) && next ? { ...cur, ...next } : cur;
  return {
    ...merchant,
    reference_id: keep(merchant.reference_id, parsed.reference_id),
    legal_business_name: keep(merchant.legal_business_name, parsed.legal_business_name),
    business_type: keep(merchant.business_type, parsed.business_type),
    tax_id: keep(merchant.tax_id, parsed.tax_id),
    incorporation: {
      ...merchant.incorporation,
      country: business.formation_country || merchant.incorporation.country,
      state: business.formation_state || merchant.incorporation.state,
      registered_address: mergeAddr(merchant.incorporation.registered_address, parsed.registered_address),
    },
    operating_address: mergeAddr(merchant.operating_address, parsed.operating_address),
  };
}

// Entities report items and notes are tagged to — the same rules as the
// backend (rules-orchestrator-py/services/tfc_onboarding/entities.py):
// the business, then each person; key = CEO, President (not Vice President)
// or an owner of 25% or more.
const BUSINESS_ENTITY = "BUSINESS";
// Everyone who is not a key person (under 25% and not CEO/President), tagged as one group.
const OTHER_OFFICERS_ENTITY = "OTHER_OFFICERS";
const OTHER_OFFICERS_LABEL = "Other Officers";
const CEO_RE = /\bCEO\b|\bchief\s+executive\b/i;
const PRESIDENT_RE = /\bpresident\b/i;
const VICE_RE = /\bvice[\s-]*president\b|\bVP\b/gi;

function keyBasis(role, ownership) {
  const r = role || "";
  const basis = [];
  if (CEO_RE.test(r)) basis.push("CEO");
  if (PRESIDENT_RE.test(r.replace(VICE_RE, ""))) basis.push("PRESIDENT");
  const pct = Number(ownership);
  if (ownership !== "" && ownership != null && !Number.isNaN(pct) && pct >= 25) basis.push("OWNER_25");
  return basis;
}

function keyEntities(legalName, officers) {
  return [
    { id: BUSINESS_ENTITY, type: "BUSINESS", name: legalName || "Business", key: true, basis: [] },
    ...(officers || []).map((o) => {
      const basis = keyBasis(o.role, o.ownership_percentage);
      const pct = Number(o.ownership_percentage);
      return {
        id: o.officer_id, type: "PERSON", name: o.full_name || "", role: o.role || "",
        ownership_percentage: o.ownership_percentage === "" || Number.isNaN(pct) ? null : pct,
        basis, key: basis.length > 0,
      };
    }),
  ];
}

const BASIS_LABEL = { CEO: "CEO", PRESIDENT: "President", OWNER_25: "Owner ≥25%" };

// What a note can be tagged to: the business, each key person, and "Other
// Officers" (one tag for everyone else) when there are any.
function tagOptions(entities) {
  const list = entities || [];
  const options = list.filter((e) => e.key);
  if (list.some((e) => e.type === "PERSON" && !e.key)) {
    options.push({ id: OTHER_OFFICERS_ENTITY, type: "OTHER_OFFICERS", name: OTHER_OFFICERS_LABEL, key: false, basis: [] });
  }
  return options;
}

// "Vasquez-Peña · CEO · 52%" — how a tag reads.
function entityLabel(e, { full = false } = {}) {
  if (!e) return "";
  if (e.type === "BUSINESS") return "Business";
  if (e.type === "OTHER_OFFICERS") return OTHER_OFFICERS_LABEL;
  const parts = [full ? e.name : (e.name || "").split(/\s+/).pop() || "Person"];
  if (e.basis?.includes("CEO")) parts.push("CEO");
  if (e.basis?.includes("PRESIDENT")) parts.push("President");
  if (e.basis?.includes("OWNER_25") && e.ownership_percentage != null) parts.push(`${e.ownership_percentage}%`);
  return parts.join(" · ");
}

function formatBytes(n) {
  if (!n && n !== 0) return "";
  return n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;
}

// Labeled form field — consistent spacing/typography around a raw input.
// The <label> wraps its control, so the control gets the label as its
// accessible name without needing ids.
function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: space[1] }}>
      <span
        style={{
          fontSize: text.sm,
          fontWeight: weight.medium,
          color: color.text.muted,
        }}
      >
        {label}
        {required && <span style={{ color: color.danger.text }}> *</span>}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: text.xs, color: color.text.tertiary }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  height: 36,
  padding: `0 ${space[3]}px`,
  borderRadius: radius.sm,
  border: `1px solid ${color.border.strong}`,
  background: color.bg.surface,
  color: color.text.primary,
  fontSize: text.md,
  fontFamily: "inherit",
  width: "100%",
};

function TextArea(props) {
  return (
    <textarea
      {...props}
      style={{ ...inputStyle, height: "auto", minHeight: 96, padding: `${space[2]}px ${space[3]}px`, lineHeight: 1.5, resize: "vertical", ...props.style }}
    />
  );
}

function Select({ children, ...props }) {
  return (
    <select {...props} style={{ ...inputStyle, ...props.style }}>
      {children}
    </select>
  );
}

// Formation documents — the state filing that created the entity. All of them
// go in the API's single business-document slot (certificate_of_incorporation);
// the type is only shown to the analyst and in the checklist.
const FORMATION_DOC_TYPES = [
  {
    entity: "Corporation",
    types: [
      { value: "articles_of_incorporation", label: "Articles of Incorporation" },
      { value: "certificate_of_incorporation", label: "Certificate of Incorporation" },
    ],
  },
  {
    entity: "Limited Liability Company (LLC)",
    types: [
      { value: "certificate_of_formation", label: "Certificate of Formation" },
      { value: "articles_of_organization", label: "Articles of Organization" },
    ],
  },
  {
    entity: "Limited Partnership",
    types: [{ value: "certificate_of_limited_partnership", label: "Certificate of Limited Partnership" }],
  },
  {
    entity: "General Partnership",
    types: [{ value: "statement_of_partnership_existence", label: "Statement of Partnership Existence" }],
  },
];

function formationDocLabel(value) {
  for (const g of FORMATION_DOC_TYPES) {
    const t = g.types.find((x) => x.value === value);
    if (t) return t.label;
  }
  return "";
}

// A sensible default from the entity type on the intake form; "" when unclear.
function suggestedFormationDocType(businessType) {
  const b = (businessType || "").toLowerCase();
  if (/\bllc\b|limited liability/.test(b)) return "certificate_of_formation";
  if (/limited partnership|\blp\b|\bllp\b/.test(b)) return "certificate_of_limited_partnership";
  if (/partnership/.test(b)) return "statement_of_partnership_existence";
  if (/corp|\binc\b|incorporated|company/.test(b)) return "articles_of_incorporation";
  return "";
}

// Drag/click file drop zone (keyboard accessible). The caller uploads on
// `onFiles` and passes back the stored file as `uploaded`; while `uploading`
// the zone shows a spinner and ignores new files.
// `doneLabel` names the done state ("Uploaded" by default; the intake form is
// only read, never stored, so it says "Selected").
function Dropzone({ label, hint, accept, uploaded, uploading, error, onFiles, onRemove, doneLabel = "Uploaded" }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const open = () => !uploading && inputRef.current && inputRef.current.click();
  const state = error ? "danger" : uploaded ? "success" : null;
  const border = over ? color.brand.solid : state ? TONES[state].border : color.border.strong;
  const bg = over ? color.brand.subtle : state === "danger" ? TONES.danger.bg : color.bg.subtle;

  return (
    <div>
      <div
        role="button"
        tabIndex={uploading ? -1 : 0}
        aria-busy={uploading || undefined}
        aria-label={uploaded ? `${label}: ${uploaded.file_name}. Click to replace.` : label}
        onClick={open}
        onKeyDown={(e) => {
          // Only the drop zone itself — Enter on the inner "Remove" button must remove.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!uploading && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px ${uploaded && !error ? "solid" : "dashed"} ${border}`,
          borderRadius: radius.lg,
          padding: space[4],
          textAlign: "center",
          background: bg,
          cursor: uploading ? "progress" : "pointer",
          transition: "border-color 120ms, background 120ms",
          outlineOffset: 2,
        }}
      >
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: space[2],
            fontSize: text.md, fontWeight: weight.medium, color: color.text.primary, wordBreak: "break-word",
          }}
        >
          {uploading && <Spinner size={14} />}
          {!uploading && uploaded && !error && <span aria-hidden="true" style={{ color: TONES.success.solid, fontWeight: 700 }}>✓</span>}
          {uploading ? "Uploading…" : uploaded ? uploaded.file_name : label}
        </div>
        {!uploaded && !uploading && (
          <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: space[1] }}>
            Click or drop a file here{hint ? ` · ${hint}` : ""}
          </div>
        )}
        {uploaded && !uploading && (
          <div style={{ fontSize: text.xs, color: TONES.success.fg, marginTop: space[1] }}>
            {doneLabel}{uploaded.size ? ` (${formatBytes(uploaded.size)})` : ""} · click to replace
            {onRemove && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                style={{
                  marginLeft: space[2], background: "none", border: "none", padding: 0,
                  color: color.text.tertiary, textDecoration: "underline", cursor: "pointer", fontSize: text.xs,
                }}
              >
                Remove
              </button>
            )}
          </div>
        )}
        {error && (
          <div role="alert" style={{ fontSize: text.xs, color: TONES.danger.fg, marginTop: space[1] }}>
            {error}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files.length) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// Renders a plain object as label/value rows, recursing one level into
// nested objects/arrays. Used wherever we display data whose exact shape
// depends on an external provider (TheKYB/ComplyAdvantage) that this UI
// can't fully predict — so nothing the API returns is ever hidden.
function KeyValueBlock({ data }) {
  if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
    return <div style={{ fontSize: text.base, color: color.text.tertiary }}>No data.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {Object.entries(data).map(([key, value]) => (
        <div
          key={key}
          style={{
            display: "flex",
            gap: space[4],
            padding: `${space[2]}px 0`,
            borderBottom: `1px solid ${color.border.subtle}`,
            fontSize: text.base,
          }}
        >
          <span style={{ color: color.text.muted, width: "38%", flexShrink: 0 }}>{key}</span>
          <span style={{ color: color.text.primary, flex: 1, wordBreak: "break-word" }}>
            {renderValue(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value.every((v) => typeof v !== "object")) return value.join(", ");
    return (
      <pre style={{ margin: 0, fontSize: text.xs, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === "object") {
    return (
      <pre style={{ margin: 0, fontSize: text.xs, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return String(value);
}

// Collapsible section for optional inputs. Closed by default; the header
// shows `summary` (e.g. "1 uploaded") so a closed section still says what's
// inside. Content stays mounted while closed, so nothing is lost on toggle.
function Accordion({ title, subtitle, summary, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        border: `1px solid ${color.border.subtle}`, borderRadius: radius.lg, background: color.bg.surface,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: space[3], padding: `${space[3]}px ${space[4]}px`,
          background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block", width: 10, color: color.text.tertiary, fontSize: text.sm,
            transform: open ? "rotate(90deg)" : "none", transition: "transform 120ms",
          }}
        >
          ▶
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}>{title}</span>
          {subtitle && (
            <span style={{ marginLeft: space[2], fontSize: text.sm, color: color.text.tertiary }}>{subtitle}</span>
          )}
        </span>
        {summary && <span style={{ fontSize: text.sm, color: color.text.tertiary, flexShrink: 0 }}>{summary}</span>}
      </button>
      <div
        hidden={!open}
        style={{ padding: `0 ${space[4]}px ${space[4]}px`, borderTop: `1px solid ${color.border.subtle}`, paddingTop: space[4] }}
      >
        {children}
      </div>
    </div>
  );
}

/* ========================================================================
 * API client (formerly api.js) — the TFC onboarding API (rules-orchestrator-py,
 * /v1/tfc-onboarding, api/tfc_onboarding_http.py) for everything except
 * reading the intake form, which goes to onboarding_intake instead (below).
 * The page never calls the onboarding API itself — screening and document
 * verification are sent server-side by the TFC backend.
 *
 *   POST /upload-document                       store one document → storage_uri
 *   POST /run-screening                         merchant screening + document verifications
 *   GET  /screening-status/{id}?documents=…     screening + document verifications (+ report)
 *   POST /retry-document-verification/{id}      resubmit documents to the same screening
 *   POST /generate-report/{id}, GET /download-report/{id}?variant=
 *   GET /list-notes/{id}, POST /add-note/{id}, PUT /edit-note/{id}/{note}, DELETE /delete-note/{id}/{note}
 *
 * Backend: REACT_APP_TFC_API_BASE (build time) when set — the production backend
 * URL, or http://localhost:9090 for `npm start` — otherwise the stage backend via
 * the same-origin "/tfc-api" nginx route.
 * ========================================================================
 */
const STAGE_TFC_API_BASE = "/tfc-api";
const TFC_API_BASE = (process.env.REACT_APP_TFC_API_BASE || STAGE_TFC_API_BASE).replace(/\/+$/, "");
const TFC = { name: "the TFC onboarding service", base: TFC_API_BASE };
const T = (path) => `/v1/tfc-onboarding${path}`;

// Arunesh's onboarding_intake pipeline (onboarding_services) -- a second,
// separate backend from TFC's own above. Reads the uploaded .docx intake
// form (canonical keys); UploadStep converts that into the shape
// mergeParsedMerchant()/mergeExtractedOfficers() expect, so Confirm/
// Screening/Report are unchanged. Same Keycloak realm ("tfcdas") as TFC's
// own service, so the page's existing authHeader() token works for both --
// no second login.
const STAGE_INTAKE_API_BASE = "/intake-api";
// The Word intake is served by the TFC backend (rules-orchestrator), so it follows
// REACT_APP_TFC_API_BASE unless REACT_APP_INTAKE_API_BASE is set explicitly.
const INTAKE_API_BASE = (process.env.REACT_APP_INTAKE_API_BASE || process.env.REACT_APP_TFC_API_BASE || STAGE_INTAKE_API_BASE).replace(/\/+$/, "");
const INTAKE = { name: "the onboarding intake service", base: INTAKE_API_BASE };

// The spec asks integrators to poll every 10–30 s. Merchant screening can sit
// in SCREENING for a while (the registry + AML secondary checks resolve
// asynchronously), so its ceiling is generous.
const POLL_EVERY_MS = Number(process.env.REACT_APP_ONBOARDING_POLL_MS) || 10000;
const SCREENING_TIMEOUT_MS = 15 * 60 * 1000;

// Per-request ceilings. Uploads and report rendering are slower than JSON calls.
const REQUEST_TIMEOUT_MS = 30 * 1000;
const UPLOAD_TIMEOUT_MS = 2 * 60 * 1000;
const REPORT_TIMEOUT_MS = 2 * 60 * 1000;

// Consecutive transient failures (network, 429, 5xx) tolerated while polling
// before the poll gives up. The record keeps processing server-side either way.
const MAX_POLL_FAILURES = 4;

// The TFC backend holds the onboarding API key; the page authenticates with
// the analyst's Keycloak access token (api/tfc_auth.py), refreshed when it is
// about to expire so a long screening poll keeps working.
async function authHeader() {
  const keycloak = getKeycloakInstance();
  try {
    await keycloak.updateToken(30);
  } catch {
    // Refresh failed (session ended): send what we have; a 401 tells the analyst to sign in again.
  }
  return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
}

function jsonHeaders() {
  return { "Content-Type": "application/json", Accept: "application/json" };
}

// Error with a user-facing message plus machine-readable fields:
//   kind    "network" | "timeout" | "http"
//   status  HTTP status (kind "http")
//   transient  true when retrying later may succeed
function apiError(message, fields) {
  const err = new Error(message);
  Object.assign(err, fields);
  return err;
}

// Turns every error body the spec documents into one readable message:
//   401/403/404/400  { detail: "string" }
//   400 pre-validation { detail: { error, message, validation_results: [...] } }
//   422 schema        { code: "UNPROCESSABLE_ENTITY", errors: [{ loc, msg, type }] }
//   422 FastAPI       { detail: [{ loc, msg, type }] }
//   500 (documents)   { detail: { error, message, action } }
function describeApiError(status, data) {
  const d = data && data.detail;
  if (d && typeof d === "object" && Array.isArray(d.validation_results)) {
    const lines = d.validation_results.flatMap((r) =>
      (r.errors || []).map((e) => `${r.document}: ${e.message}`)
    );
    return [d.message, ...lines].filter(Boolean).join(" ");
  }
  const fieldErrors = (list) =>
    list
      .map((e) => {
        const where = (e.loc || []).filter((p) => p !== "body").join(".");
        return where ? `${where}: ${e.msg}` : e.msg;
      })
      .join("; ");
  if (data && data.code === "UNPROCESSABLE_ENTITY" && Array.isArray(data.errors)) return fieldErrors(data.errors);
  if (Array.isArray(d)) return fieldErrors(d);
  if (d && typeof d === "object") return [d.message, d.action].filter(Boolean).join(" ");
  if (typeof d === "string") return d;
  if (data && data.message) return data.message;
  return null;
}

function statusMessage(status, svc) {
  if (status === 401) return `${cap(svc.name)} rejected the request (401). The service isn't configured correctly — contact support.`;
  if (status === 403) return `${cap(svc.name)} refused access for this subscriber (403).`;
  if (status === 404) return "The requested record was not found (404).";
  if (status === 413) return "The file is too large for the server (413).";
  if (status === 429) return `${cap(svc.name)} is rate-limiting requests (429). Try again in a moment.`;
  if (status >= 500) return `${cap(svc.name)} had a problem (${status}). Try again in a moment.`;
  return `Request failed (${status}).`;
}
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// fetch with a timeout and readable failures. A refused connection, DNS
// failure or CORS block all surface as TypeError from fetch — the message
// then names the service and URL so it's obvious what isn't reachable.
async function send(svc, path, init = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const headers = { ...(init.headers || {}), ...(await authHeader()) };
    return await fetch(`${svc.base}${path}`, { ...init, headers, signal: ctrl ? ctrl.signal : undefined });
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw apiError(`${cap(svc.name)} didn't respond within ${Math.round(timeoutMs / 1000)} seconds.`, {
        kind: "timeout", transient: true,
      });
    }
    throw apiError(
      `Couldn't reach ${svc.name} at ${svc.base}. Check that it's running and reachable from this browser` +
        (typeof navigator !== "undefined" && navigator.onLine === false ? " (you appear to be offline)." : "."),
      { kind: "network", transient: true }
    );
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function asJson(res, svc) {
  const raw = await res.text().catch(() => "");
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = {}; // HTML error page from a proxy / tunnel
    }
  }
  if (!res.ok) {
    const detail = describeApiError(res.status, data);
    const message = res.status >= 500 && detail
      ? `${cap(svc.name)} had a problem (${res.status}): ${detail.replace(/\.$/, "")}. Try again in a moment.`
      : detail || statusMessage(res.status, svc);
    throw apiError(message, {
      kind: "http",
      status: res.status,
      body: data,
      transient: res.status === 429 || res.status >= 500,
      // Pre-validation failures carry per-document detail the UI shows inline.
      validationResults: data && data.detail && data.detail.validation_results ? data.detail.validation_results : undefined,
    });
  }
  if (!raw) return {};
  if (data && typeof data === "object") return data;
  throw apiError(`${cap(svc.name)} returned an unexpected response.`, { kind: "http", status: res.status });
}

// Stores one document, returns { storage_uri, file_name, content_type, size }.
async function uploadDocument(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await send(TFC, T("/upload-document"), { method: "POST", body: form }, UPLOAD_TIMEOUT_MS);
  const out = await asJson(res, TFC);
  if (!out.storage_uri) throw apiError("The upload didn't return a file link. Try again.", { kind: "http" });
  return out;
}

// Word questionnaire path: onboarding_intake's prefill(), canonical keys only.
async function intakePrefill(file) {
  const form = new FormData();
  form.append("file", file);
  return asJson(await send(INTAKE, "/onboarding/intake/prefill", { method: "POST", body: form }, UPLOAD_TIMEOUT_MS), INTAKE);
}

const BUSINESS_TYPE_LABELS = {
  individual: "Individual", joint: "Joint", sole_proprietor: "Sole Proprietor",
  corporation: "Corporation", llc: "LLC", partnership: "Partnership",
  trust: "Trust", nonprofit: "Nonprofit", government: "Government Entity", other: "Other",
};

const INTAKE_NA = "N/A"; // onboarding_intake's canonical.NA sentinel value

function legacyAddress(v) {
  if (!v || v === INTAKE_NA) return { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };
  return { line1: v.line1 || "", line2: "", city: v.city || "", state: v.region || "", postal_code: v.postal_code || "", country: v.country || "" };
}

// "Jordan Avery Blake (fka Jordan A. Reyes)" -> "Jordan Avery Blake"
const primaryPersonName = (raw) => (raw || "").split(/\s*\(|\s+(?:aka|a\/k\/a|fka|f\/k\/a|dba|d\/b\/a)\s+/i)[0].trim();

function guessIdDocType(text) {
  const t = (text || "").toLowerCase();
  if (t.includes("passport")) return "PASSPORT";
  if (t.includes("driver")) return "DRIVERS_LICENSE";
  return text ? "ID_CARD" : "";
}

// ownership.beneficial_owners / control_person / authorized_signers only
// give structured name + dob + ownership% per person -- but the question
// asks for "name, DOB, address, ID, % ownership" in one answer, so whatever
// else the respondent wrote (commonly a city/country) survives in that
// person's own `raw` text. COUNTRIES (ConfirmReadingStep, below) is reused
// to pull a country out of it on a best-effort basis: ConfirmReadingStep's
// officer table is read-only (ValueGrid, not inputs), so a field this blank
// isn't something the analyst can fix there -- it would be a dead end, not
// a review step, for every officer who isn't the form's primary signer.
function guessCountryFromText(text) {
  if (!text) return "";
  const low = text.toLowerCase();
  const hit = COUNTRIES.find(([, name]) => low.includes(name.toLowerCase()));
  return hit ? hit[0] : "";
}

// "Certified, J. Blake 10/01/2026" -> "2026-10-01" (US month-first, as the form prints it)
function certificationDate(text) {
  const m = (text || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : "";
}

// Converts prefill()'s canonical `values` into exactly the shape
// mergeParsedMerchant() / mergeExtractedOfficers() — and everything
// downstream of them — already expect.
//
// PEP: the Word form asks it once for the customer, owners and signers
// (sanctions.pep), so that one answer is each person's declaration. A "Yes"
// marks the people named in sanctions.pep_detail, or everyone when no name
// matches, so a declared PEP is never dropped. Screening still makes the
// authoritative PEP determination.
//
// "entity" is only for businesses: an owner the parser typed "entity" whose name
// is a business name (LLC, Inc, Ltd, ...) isn't a person to screen or ask for
// an ID, and is listed as a warning instead. If the name looks like a person's,
// the type is ignored and they stay an officer, so a person is never dropped
// from screening by a mis-typed answer.
const BUSINESS_NAME_RE = new RegExp(
  "\\b(llc|l\\.l\\.c|inc|incorporated|corp|corporation|co|company|ltd|limited|lp|llp|lllp|plc|" +
    "gmbh|ag|s\\.?a|b\\.?v|n\\.?v|pty|holdings?|group|partners|partnership|trust|fund|foundation|" +
    "bank|capital|ventures|enterprises|associates|international|services|technologies)\\b\\.?",
  "i"
);
const looksLikeBusiness = (name) => BUSINESS_NAME_RE.test(name || "");

function canonicalToLegacyIntake(values, warnings) {
  const v = (key) => {
    const x = values[key];
    return x === INTAKE_NA ? null : x;
  };

  const entityName = v("entity.legal_name") || "";
  const [legalName, dba] = entityName.split(/\s+dba\s+/i);
  const formation = v("entity.formation") || {};
  const principalAddr = legacyAddress(v("entity.principal_address"));
  const contact = v("individual.contact") || {};
  const citizenship = v("individual.citizenship_tax_residence") || {};

  const pep = v("sanctions.pep") || {};
  const pepAnswered = typeof pep.answer === "boolean";
  const pepDetail = (v("sanctions.pep_detail") || "").trim();
  const activities = new Set((v("business.high_risk_activities") || {}).selected || []);
  const msbRegistration = v("program.fincen_msb") || {};
  const licensed = v("entity.regulated_licensed") || {};
  const description = v("business.description") || "";
  const naics = (v("business.naics") || {}).text || "";
  const certification = typeof v("ownership.certification") === "string" ? v("ownership.certification").trim() : "";
  const companyOwners = [];
  const typeConflicts = [];
  const typedEntity = (p) => (p.type || "").toLowerCase() === "entity";
  // A business owner only when typed "entity" AND named like a business.
  const isEntity = (p) => {
    if (!typedEntity(p)) return false;
    if (looksLikeBusiness(p.name)) return true;
    typeConflicts.push(p);
    return false;
  };

  const officers = new Map();
  const touch = (name, patch) => {
    const key = (name || "").trim().toLowerCase();
    if (!key) return;
    officers.set(key, { ...(officers.get(key) || { full_name: name.trim() }), ...patch });
  };

  const primaryName = primaryPersonName(v("individual.full_name"));
  if (primaryName) {
    const primaryCountry = (citizenship.countries || [])[0] || "";
    touch(primaryName, {
      dob: v("individual.dob") || "",
      email: (contact.emails || [])[0] || "",
      role: v("individual.occupation") || "",
      id_document_type: guessIdDocType(v("individual.photo_id")),
      ...(primaryCountry ? { nationality: primaryCountry, country_of_residence: primaryCountry } : {}),
    });
  }
  for (const p of (v("ownership.beneficial_owners") || {}).people || []) {
    if (isEntity(p)) {
      companyOwners.push(p);
      continue;
    }
    const country = guessCountryFromText(p.raw);
    touch(p.name, {
      dob: p.dob || "",
      ownership_percentage: p.ownership_pct ?? "",
      ...(country ? { nationality: country, country_of_residence: country } : {}),
    });
  }
  for (const p of (v("ownership.control_person") || {}).people || []) {
    if (isEntity(p)) continue;
    const country = guessCountryFromText(p.raw);
    touch(p.name, { role: p.title || "", ...(country ? { nationality: country, country_of_residence: country } : {}) });
  }
  for (const p of (v("access.authorized_signers") || {}).people || []) {
    if (isEntity(p)) continue;
    const country = guessCountryFromText(p.raw);
    touch(p.name, { role: p.title || "", ...(country ? { nationality: country, country_of_residence: country } : {}) });
  }

  const people = Array.from(officers.values());
  if (pepAnswered) {
    const detail = pepDetail.toLowerCase();
    const named = pep.answer ? people.filter((o) => detail.includes(o.full_name.toLowerCase())) : [];
    for (const o of people) {
      o.has_detail_block = true;
      o.pep_declared = pep.answer && (named.length === 0 || named.includes(o));
      o.pep_detail = o.pep_declared ? pepDetail : "";
    }
  }

  const licenses = [
    licensed.answer ? licensed.explanation : "",
    msbRegistration.answer && msbRegistration.explanation ? `FinCEN MSB: ${msbRegistration.explanation}` : "",
  ].filter(Boolean).join("; ");
  const typeConflictChecks = [...new Set(typeConflicts.map((p) => p.name).filter(Boolean))].map((name) => ({
    severity: "warning",
    message: `${name} was read as a company but looks like a person, so they are kept as a person and screened. Check the ownership answer.`,
  }));
  const companyOwnerChecks = companyOwners.map((p) => ({
    severity: "warning",
    message: `${p.name || "A company"}${p.ownership_pct != null ? ` (${p.ownership_pct}%)` : ""} is a company owner, not a person: it isn't screened here. List and screen the people who own or control it.`,
  }));

  // Sections 7–8 of the Word form as the report's applicant disclosures:
  // {number, question, answer: YES | NO | REVIEW, explanation}. 8.4 (PEP details)
  // is attached to 8.3; details given while 8.3 is not Yes become an 8.4 REVIEW.
  const typedNone = (t) => /^(none|n\/?a|nil|no|-+)\.?$/i.test((t || "").trim());
  const disclosures = [];
  for (const [number, key, question] of DISCLOSURE_QUESTIONS) {
    const a = v(key);
    if (!a || typeof a.answer !== "boolean") continue;
    const parts = [];
    if (a.explanation) parts.push(a.explanation);
    if ((a.countries || []).length) parts.push(`Countries: ${a.countries.join(", ")}`);
    if (number === "8.3" && a.answer && pepDetail && !typedNone(pepDetail)) parts.push(`8.4: ${pepDetail}`);
    disclosures.push({ number, question, answer: a.answer ? "YES" : "NO", explanation: parts.join("; ") });
  }
  if (pepDetail && !typedNone(pepDetail) && pep.answer !== true) {
    disclosures.push({ number: "8.4", question: "PEP details given although 8.3 is not Yes", answer: "REVIEW", explanation: pepDetail });
  }

  // Declared cross-border counterparties: one per country of international wires (5.7 / 5.8).
  const counterparties = [];
  for (const [key, label] of [["activity.wires_in_intl", "International wires in"], ["activity.wires_out_intl", "International wires out"]]) {
    const a = v(key) || {};
    for (const country of a.countries || []) {
      counterparties.push({ name: label, country, relationship: label, annual_volume: monthlyVolume(a.monthly_value) });
    }
  }

  return {
    legal_business_name: (legalName || "").trim(),
    dba: (dba || "").trim(),
    business_type: BUSINESS_TYPE_LABELS[(v("account.type") || {}).selected] || "",
    tax_id: (v("entity.tin") || {}).tin || "",
    business: {
      formation_country: formation.jurisdiction_country || "",
      formation_state: formation.jurisdiction_region || "",
      formation_date: formation.formation_date || "",
      nature_of_business: [description, naics && `NAICS ${naics}`].filter(Boolean).join(" — "),
      licenses,
      is_msb: activities.has("money_transmission") || msbRegistration.answer === true,
      virtual_assets: activities.has("virtual_currency"),
      payment_processor: activities.has("payment_processing") || activities.has("tppp"),
    },
    certification: certification
      ? { signed: true, signer_name_title: certification, date: certificationDate(certification) }
      : { signed: false },
    registered_address: principalAddr,
    operating_address: principalAddr,
    disclosures,
    cross_border: {
      sanctioned_country_activity: (v("geo.ofac_jurisdictions") || {}).answer === true,
      counterparties,
    },
    officers: people,
    page_count: 1,
    form_checks: [...warningsToChecks(warnings), ...companyOwnerChecks, ...typeConflictChecks],
  };
}

// Word form questions reported as applicant disclosures: [number, canonical key, question].
const DISCLOSURE_QUESTIONS = [
  ["7.2", "geo.ofac_jurisdictions", "Business with OFAC-sanctioned or embargoed jurisdictions"],
  ["7.3", "geo.fatf_jurisdictions", "Transacts with FATF high-risk or increased-monitoring jurisdictions"],
  ["7.4", "geo.russia_belarus", "Transacts with Russia or Belarus"],
  ["7.5", "geo.border_hidta_hifca", "Operates in the U.S.–Mexico border region or a HIDTA / HIFCA area"],
  ["8.1", "sanctions.listed", "Customer, owner, director, officer or signer on a sanctions list"],
  ["8.2", "sanctions.fifty_percent_rule", "Entity 50% or more owned by sanctioned persons"],
  ["8.3", "sanctions.pep", "Current or former Politically Exposed Person, or family member / close associate"],
  ["8.5", "sanctions.adverse_history", "Criminal charges, enforcement, penalties, bankruptcy or financial-crime litigation"],
  ["8.6", "sanctions.prior_account_closure", "A bank has closed an account of the customer or principals"],
];

// {min, max} USD per month -> "$8.0M / month" or "$10.0M–$40.0M / month".
function monthlyVolume(mv) {
  if (!mv || (mv.min == null && mv.max == null)) return "";
  const fmt = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(1)}K` : `$${n}`);
  const lo = mv.min ?? mv.max, hi = mv.max ?? mv.min;
  return `${lo === hi ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}`} / month`;
}

// prefill()'s document-level warnings (a question wasn't found, the form's
// wording drifted from the known template, a checkbox looked ambiguous, ...)
// -> the {severity, message} shape form_checks already renders, both on this
// screen's "N issues to review" summary and Confirm's "Integrity checks".
const WARNING_SEVERITY = {
  question_missing: "warning",
  question_changed: "warning",
  question_duplicate: "warning",
  checkbox_mismatch: "warning",
  question_unknown: "info",
};
function warningsToChecks(warnings) {
  return (warnings || []).map((w) => ({ severity: WARNING_SEVERITY[w.code] || "warning", message: w.message }));
}

const postJson = async (path, body, timeoutMs) =>
  asJson(await send(TFC, T(path), { method: "POST", headers: jsonHeaders(), body: JSON.stringify(body) }, timeoutMs), TFC);

// Submits the merchant screening and each document's verification (linked to
// it) server-side. → { application_id, verification_key, status,
//   documents: { merchant | officer_id: { application_id } | { error: { message, validation_results } } } }
async function runScreening(screening, documents) {
  const out = await postJson("/run-screening", { screening, documents });
  if (!Number.isInteger(out.application_id)) {
    throw apiError("Screening was accepted but returned no application id.", { kind: "http" });
  }
  return out;
}

// → { screening, documents: { target: record }, report }
async function getScreeningStatus(applicationId, documentIds) {
  const docs = Object.entries(documentIds || {}).filter(([, id]) => id).map(([k, id]) => `${k}:${id}`).join(",");
  const q = docs ? `?documents=${encodeURIComponent(docs)}` : "";
  return asJson(await send(TFC, T(`/screening-status/${applicationId}${q}`)), TFC);
}

// Resubmits documents to the same screening → { documents: { target: result } }.
async function retryDocumentVerification(applicationId, documents) {
  return postJson(`/retry-document-verification/${applicationId}`, { documents });
}

// Builds both PDFs from the server-side screening and verification records,
// stores them (replacing the previous pair) and returns the report metadata.
async function generateReport(applicationId, body) {
  return postJson(`/generate-report/${applicationId}`, body, REPORT_TIMEOUT_MS);
}

async function fetchReportPdf(applicationId, variant) {
  const res = await send(TFC, T(`/download-report/${applicationId}?variant=${variant}`), {}, REPORT_TIMEOUT_MS);
  if (!res.ok) await asJson(res, TFC); // throws with a readable message
  const blob = await res.blob();
  if (!blob.size) throw apiError("The report file came back empty. Regenerate it.", { kind: "http" });
  return blob;
}

// Notes on the onboarding (shared Idenza_Notes table), tagged to the business
// and/or key persons.
async function listNotes(applicationId) {
  const out = await asJson(await send(TFC, T(`/list-notes/${applicationId}`)), TFC);
  return out.notes || [];
}

async function saveNote(applicationId, noteId, text, tags) {
  const path = noteId ? `/edit-note/${applicationId}/${noteId}` : `/add-note/${applicationId}`;
  const res = await send(TFC, T(path), { method: noteId ? "PUT" : "POST", headers: jsonHeaders(), body: JSON.stringify({ text, tags }) });
  return asJson(res, TFC);
}

async function deleteNote(applicationId, noteId) {
  const res = await send(TFC, T(`/delete-note/${applicationId}/${noteId}`), { method: "DELETE" });
  if (!res.ok) await asJson(res, TFC); // throws with a readable message
}

// Sleeps `ms`, waking early when `signal.cancelled` is set.
async function sleep(ms, signal) {
  const until = Date.now() + ms;
  while (Date.now() < until) {
    if (signal && signal.cancelled) return;
    await new Promise((r) => setTimeout(r, Math.min(250, until - Date.now())));
  }
}

const cancelledError = () => apiError("Cancelled.", { cancelled: true });

// Polls `fetcher(id)` until `isTerminal(result)`, or `timeoutMs` elapses.
// `onUpdate` receives every intermediate record (for live status display).
// `signal.cancelled = true` stops polling (superseded run / page closed).
// Transient failures (network, 429, 5xx) are retried with back-off; a
// non-transient one (401/403/404) ends the poll immediately.
async function pollUntilTerminal(fetcher, applicationId, isTerminal, opts = {}) {
  const { timeoutMs = SCREENING_TIMEOUT_MS, onUpdate, onRetry, signal } = opts;
  const start = Date.now();
  let failures = 0;
  let last = null;
  for (;;) {
    if (signal && signal.cancelled) throw cancelledError();
    let result;
    try {
      result = await fetcher(applicationId);
      failures = 0;
    } catch (e) {
      if (signal && signal.cancelled) throw cancelledError();
      if (!e.transient || failures + 1 >= MAX_POLL_FAILURES) throw e;
      failures += 1;
      if (onRetry) onRetry(e, failures);
      await sleep(Math.min(POLL_EVERY_MS * 2 ** failures, 60000), signal);
      continue;
    }
    if (signal && signal.cancelled) throw cancelledError();
    last = result;
    if (onUpdate) onUpdate(result);
    if (isTerminal(result)) return result;
    if (Date.now() - start > timeoutMs) {
      throw apiError("Still processing after a long wait. It may yet complete — check again in a few minutes.", {
        timeout: true, lastResult: last,
      });
    }
    await sleep(POLL_EVERY_MS, signal);
  }
}

// SCREENED or ERROR are terminal for both flows. PENDING_DOCUMENT_VERIFICATION
// is NOT an error — keep polling (spec).
const isDocVerificationTerminal = (r) => !!r && (r.status === "SCREENED" || r.status === "ERROR");
const isOnboardingTerminal = (r) => !!r && (r.status === "SCREENED" || r.status === "ERROR");

/* ========================================================================
 * Request payload builders (formerly payloads.js) — plus the fingerprints
 * used to decide whether something needs to be (re)submitted.
 * ========================================================================
 */
const BUSINESS_DOC_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ID_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const SELFIE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const pickType = (doc, allowed) => (doc && allowed.includes(doc.content_type) ? doc.content_type : undefined);
const docRef = (doc, allowed) =>
  doc && doc.storage_uri
    ? { storage_uri: doc.storage_uri, file_name: doc.file_name, content_type: pickType(doc, allowed) }
    : undefined;
const trim = (v) => (typeof v === "string" ? v.trim() : v);

const officerHasDocs = (o) => !!(o.documents?.id?.storage_uri && o.documents?.selfie?.storage_uri);
const hasCoi = (m) => !!m.documents?.certificate_of_incorporation?.storage_uri;

// POST /v1/onboarding/merchant body (MerchantOnboardingPayload). Screening
// only — `documents` is deprecated/ignored on this endpoint, so it is not
// sent; documents go to /v1/verification/documents, linked by key.
function buildScreeningPayload(data) {
  const m = data.merchant;
  const merchant = {
    merchant_id: m.merchant_id,
    legal_business_name: trim(m.legal_business_name) || "",
    incorporation: resolveIncorporation(m),
  };
  if (trim(m.business_type)) merchant.business_type = trim(m.business_type);
  if (m.contact_email && m.contact_email.includes("@")) merchant.contact_email = m.contact_email.trim();
  if (trim(m.tax_id)) merchant.tax_id = trim(m.tax_id);
  if (trim(m.registration_number)) merchant.registration_number = trim(m.registration_number);
  if (!addressIsEmpty(m.operating_address)) merchant.operating_address = cleanAddress(m.operating_address);

  return {
    application_type: "MERCHANT_MSB",
    ...(trim(m.reference_id) ? { reference_id: trim(m.reference_id) } : {}),
    merchant,
    officers: data.officers.map((o) => {
      const officer = {
        officer_id: o.officer_id,
        full_name: trim(o.full_name) || "",
        role: trim(o.role) || "",
        ownership_percentage: Number(o.ownership_percentage),
        dob: o.dob,
        nationality: o.nationality || "",
      };
      if (o.country_of_residence) officer.country_of_residence = o.country_of_residence;
      if (o.email && o.email.includes("@")) officer.email = o.email.trim();
      return officer;
    }),
  };
}

function merchantDocPayload(data, key) {
  const m = data.merchant;
  const inc = resolveIncorporation(m);
  const docs = {};
  for (const slot of ["certificate_of_incorporation", "irs_ein_letter", "business_license", "state_licenses_bundle"]) {
    const ref = docRef(m.documents[slot], BUSINESS_DOC_TYPES);
    if (ref) docs[slot] = ref;
  }
  return {
    application_type: "MERCHANT_DOC",
    ...(key ? { verification_key: key } : {}),
    external_ref: m.merchant_id,
    reference_id: trim(m.reference_id) || undefined,
    company_name: trim(m.legal_business_name),
    registration_number: trim(m.registration_number) || undefined,
    country: inc.country,
    state: inc.state || undefined,
    merchant_documents: docs,
  };
}

function officerDocPayload(data, o, key) {
  const id = {
    ...docRef(o.documents.id, ID_CONTENT_TYPES),
    document_type: o.id_doc_type,
    doc_type: o.id_doc_type, // deprecated alias — keeps older backends working
  };
  if (o.documents.id_back?.storage_uri) {
    id.back_storage_uri = o.documents.id_back.storage_uri;
    const bt = pickType(o.documents.id_back, ID_CONTENT_TYPES);
    if (bt) id.back_content_type = bt;
  }
  return {
    application_type: "INDIVIDUAL_DOC",
    ...(key ? { verification_key: key, subject_ref: o.officer_id } : {}),
    external_ref: o.officer_id,
    full_name: trim(o.full_name),
    dob: o.dob || undefined,
    nationality: o.nationality || undefined,
    country_of_residence: o.country_of_residence || undefined,
    individual_documents: { id, selfie: docRef(o.documents.selfie, SELFIE_TYPES) },
  };
}

// Stable string of what would be sent. Two equal fingerprints = the API would
// receive an identical request, so nothing needs to be resubmitted.
const fingerprint = (payload) => JSON.stringify(payload);
const screeningFingerprint = (data) => fingerprint(buildScreeningPayload(data));

// Every document verification the current data calls for, keyed
// "merchant" | officer_id → payload.
function docTargets(data, key) {
  const out = {};
  if (hasCoi(data.merchant)) out.merchant = merchantDocPayload(data, key);
  for (const o of data.officers) if (officerHasDocs(o)) out[o.officer_id] = officerDocPayload(data, o, key);
  return out;
}

/* ========================================================================
 * Status view mappers (formerly status.js) — turns API statuses/decisions
 * into { tone, label, busy } for display.
 * ========================================================================
 */
const IN_FLIGHT = new Set(["SUBMITTING", "RECEIVED", "SCREENING", "PENDING_DOCUMENT_VERIFICATION"]);

// Application-level screening outcome.
function screeningView(rec) {
  if (!rec) return { tone: "neutral", label: "Not started" };
  if (rec.status === "ERROR") return { tone: "danger", label: "Could not complete" };
  if (rec.status === "SCREENED") {
    if (rec.decision === "APPROVE") return { tone: "success", label: "Approved" };
    if (rec.decision === "REVIEW") return { tone: "warning", label: "Manual review" };
    if (rec.decision === "DENY") return { tone: "danger", label: "Declined" };
    return { tone: "neutral", label: "Complete" };
  }
  return { tone: "info", label: "In progress", busy: true };
}

// Officer-level KYC decision (officer scale adds EDD and ERROR).
function officerKycView(k, stillRunning) {
  if (!k) return stillRunning ? { tone: "info", label: "Screening", busy: true } : { tone: "neutral", label: "No result" };
  switch (k.decision) {
    case "APPROVE": return { tone: "success", label: "Clear" };
    case "REVIEW": return { tone: "warning", label: "Needs review" };
    case "EDD": return { tone: "warning", label: "Enhanced due diligence" };
    case "DENY": return { tone: "danger", label: "Declined" };
    case "ERROR": return { tone: "danger", label: "Could not screen" };
    default: return stillRunning ? { tone: "info", label: "Screening", busy: true } : { tone: "neutral", label: "No result" };
  }
}

// Document verification record (or a local SUBMITTING / NOT_SUBMITTED one).
function docView(rec) {
  if (!rec) return { tone: "neutral", label: "Not started" };
  if (rec.status === "NOT_SUBMITTED") return { tone: "danger", label: "Not submitted" };
  if (rec.timedOut) return { tone: "warning", label: "Still verifying" };
  if (rec.status === "SUBMITTING") return { tone: "info", label: "Submitting", busy: true };
  if (!isDocVerificationTerminal(rec)) return { tone: "info", label: "Verifying", busy: true };
  switch (rec.decision) {
    case "APPROVE": return { tone: "success", label: "Verified" };
    case "REVIEW": return { tone: "warning", label: "Needs review" };
    case "DENY":
    case "REJECT": return { tone: "danger", label: "Failed" };
    default: return { tone: "danger", label: "Could not verify" };
  }
}

// kyb_summary.kyb_registry_check / aml_check: pending|completed|failed|error|skipped.
function checkView(marker, screeningDone) {
  const s = (marker && marker.status) || null;
  if (s === "completed") return { tone: "success", label: "Completed" };
  if (s === "skipped") return { tone: "neutral", label: "Skipped" };
  if (s === "failed" || s === "error") return { tone: "danger", label: "Failed" };
  if (s === "pending" || (!s && !screeningDone)) return { tone: "info", label: "Running", busy: true };
  return { tone: "neutral", label: "Not run" };
}

const RISK_VIEW = {
  LOW: { tone: "success", label: "Low risk" },
  MEDIUM: { tone: "warning", label: "Medium risk" },
  HIGH: { tone: "danger", label: "High risk" },
};

const EDD_TEXT = {
  BUSINESS_HIGH_RISK: "Business risk indicators",
  OFFICER_PEP: "Politically exposed person",
  OFFICER_SANCTIONS_HIT: "Confirmed sanctions hit",
  OFFICER_SANCTIONS_OR_TF: "Sanctions / terrorist-financing exposure",
  OFFICER_HIGH_RISK: "Elevated owner risk",
  SECONDARY_CHECK_UNSUPPORTED_COUNTRY: "Registry and AML checks not available for this country / state",
  SCREENING_UNAVAILABLE: "Some checks could not be completed",
};
const eddTone = (code) => (String(code).startsWith("SCREENING_UNAVAILABLE") ? "danger" : "warning");

/* ========================================================================
 * Submission checklist (formerly checklist.js) — what this onboarding needs,
 * grouped by what uses it.
 *
 * Each item is derived automatically. derive() returns { status, detail }:
 *   status  "form"     — provided by the parsed intake form
 *           "upload"   — provided by an uploaded document
 *           "declared" — the applicant ticked it as attached on the form, but
 *                        nothing is in the system yet (NOT counted as provided)
 *           false      — missing
 *   detail  optional one-liner saying exactly who / what is missing
 * An item may also carry a static `hint`, always shown under its label.
 *
 * Screening and Document verification items are REQUIRED; Enhanced due
 * diligence items are RECOMMENDED (they matter for higher-risk cases). The
 * analyst can override any tick. Labels double as the keys sent to the
 * report service, so keep them stable.
 * ========================================================================
 */
const FORMATION_DOC_NAMES = FORMATION_DOC_TYPES.flatMap((g) => g.types.map((t) => t.label));

const normChecklist = (s) => (s || "").toLowerCase();

// Section 7 / 8 lines the applicant ticked as attached.
function declared(parsed, ...patterns) {
  const items = [
    ...(parsed?.supporting_documents_declared || []),
    ...(parsed?.documents_returned?.checklist || []),
  ];
  return items.some((i) => i.checked && patterns.some((p) => p.test(normChecklist(i.item))));
}

const hasIdAndSelfie = (o) => !!(o.documents?.id?.storage_uri && o.documents?.selfie?.storage_uri);
const checklistNames = (people) => people.map((o) => o.full_name || "Name missing").join(", ");
const ok = (status, detail) => ({ status, detail });
const missing = (detail) => ({ status: false, detail });

const CHECKLIST_GROUPS = [
  {
    title: "Screening",
    note: "Needed for business and owner screening",
    required: true,
    items: [
      {
        label: "Legal name and registered address",
        derive: ({ parsed }) => {
          if (!parsed?.legal_business_name) return missing("Legal business name missing");
          if (!addressIsComplete(parsed?.registered_address)) return missing("Registered address incomplete");
          return ok("form");
        },
      },
      {
        label: "Tax ID / EIN",
        derive: ({ parsed }) => (parsed?.tax_id ? ok("form") : missing()),
      },
      {
        label: "Country and state of formation",
        derive: ({ parsed }) => {
          const b = parsed?.business || {};
          if (!b.formation_country) return missing();
          if (b.formation_country === "US" && !b.formation_state) return missing("State of formation not found");
          return ok("form");
        },
      },
      {
        label: "Owners and control persons with ownership %, date of birth and nationality",
        derive: ({ parsed }) => {
          const os = parsed?.officers || [];
          if (!os.length) return missing("No owners or control persons found");
          const incomplete = os.filter((o) => !(o.full_name && o.dob && o.nationality && o.ownership_percentage != null));
          return incomplete.length ? missing(`Incomplete: ${checklistNames(incomplete)}`) : ok("form", `${os.length} listed`);
        },
      },
      {
        label: "PEP declaration for each owner and control person",
        derive: ({ parsed }) => {
          const os = parsed?.officers || [];
          if (!os.length) return missing();
          const without = os.filter((o) => !o.has_detail_block);
          return without.length
            ? missing(`${without.length} of ${os.length} missing: ${checklistNames(without)}`)
            : ok("form");
        },
      },
      {
        label: "Signed certification",
        derive: ({ parsed }) => (parsed?.certification?.signed ? ok("form") : missing()),
      },
    ],
  },
  {
    title: "Document verification",
    note: "Needed to verify the business and each person",
    required: true,
    items: [
      {
        label: "Formation document",
        hint: `Accepted: ${FORMATION_DOC_NAMES.join(", ")}`,
        derive: ({ parsed, merchant, formationDocType }) => {
          if (merchant?.documents?.certificate_of_incorporation?.storage_uri) {
            return ok("upload", formationDocLabel(formationDocType) || undefined);
          }
          return declared(
            parsed,
            /certificate of (formation|incorporation|limited partnership)/,
            /articles of (incorporation|organi[sz]ation)/,
            /statement of partnership existence/,
            /formation document/,
          )
            ? { status: "declared", detail: "Declared as attached — upload it under Formation document" }
            : missing("Upload it under Formation document");
        },
      },
      {
        label: "Government-issued photo ID for each owner and control person",
        derive: ({ parsed, officers }) => {
          const os = officers || [];
          const done = os.filter(hasIdAndSelfie);
          if (os.length && done.length === os.length) return ok("upload", `${done.length} of ${os.length} uploaded`);
          const pending = os.filter((o) => !hasIdAndSelfie(o));
          const who = os.length ? `${done.length} of ${os.length} uploaded — still needed: ${checklistNames(pending)}` : undefined;
          return declared(parsed, /photo id/, /passport or id/)
            ? { status: "declared", detail: who || "Declared as attached — upload under ID and selfie" }
            : missing(who);
        },
      },
    ],
  },
  {
    title: "Enhanced due diligence",
    note: "Supporting evidence for higher-risk cases",
    required: false,
    items: [
      { label: "Beneficial ownership certification (FinCEN CDD)", derive: ({ parsed }) => (declared(parsed, /beneficial ownership/) ? ok("form") : missing()) },
      { label: "Proof of physical business address", derive: ({ parsed }) => (declared(parsed, /proof of physical business address/) ? ok("form") : missing()) },
      {
        label: "Business licences or MSB registration",
        derive: ({ parsed }) => (declared(parsed, /licen[cs]es?, permits|msb registration/) || parsed?.business?.licenses ? ok("form") : missing()),
      },
      { label: "Written AML/OFAC policy", derive: ({ parsed }) => (declared(parsed, /aml\/ofac policy/) ? ok("form") : missing()) },
      { label: "Financial statements or tax returns (2 years)", derive: ({ parsed }) => (declared(parsed, /financial statements|tax returns/) ? ok("form") : missing()) },
    ],
  },
];

const CHECKLIST_ITEMS = CHECKLIST_GROUPS.flatMap((g) => g.items.map((i) => i.label));
const REQUIRED_ITEMS = new Set(CHECKLIST_GROUPS.filter((g) => g.required).flatMap((g) => g.items.map((i) => i.label)));

const PROVIDED = new Set(["form", "upload"]);

// → { [label]: { status, detail } }
function deriveChecklist(data) {
  const ctx = {
    parsed: data.intakeFormParsed, merchant: data.merchant, officers: data.officers,
    formationDocType: data.formationDocType || suggestedFormationDocType(data.merchant?.business_type),
  };
  const out = {};
  for (const g of CHECKLIST_GROUPS) for (const i of g.items) out[i.label] = i.derive(ctx) || missing();
  return out;
}

// Effective ticks: an analyst override wins, otherwise "provided" (form or
// upload). A "declared" document is NOT provided until it is uploaded.
function effectiveChecklist(data, auto = deriveChecklist(data)) {
  const overrides = data.checklistOverrides || {};
  return Object.fromEntries(
    CHECKLIST_ITEMS.map((l) => [l, l in overrides ? overrides[l] : PROVIDED.has(auto[l]?.status)])
  );
}

// → { required: n, recommended: n } items not ticked.
function missingCounts(checklist) {
  let required = 0, recommended = 0;
  for (const [label, on] of Object.entries(checklist)) {
    if (on) continue;
    if (REQUIRED_ITEMS.has(label)) required += 1;
    else recommended += 1;
  }
  return { required, recommended };
}

/* ========================================================================
 * Stepper (formerly Stepper.jsx)
 * ========================================================================
 */
const STEPS = [
  { key: "upload", label: "Upload", built: true },
  { key: "confirm", label: "Confirm reading", built: true },
  { key: "screening", label: "Screening", built: true },
  { key: "report", label: "Report", built: true },
];

// `status` marks a step "busy" (spinner) or "error" (red dot), e.g. while
// screening runs in the background on another step.
function Stepper({ activeKey, furthestIndex, onSelect, status = {} }) {
  return (
    <div style={{ display: "flex", gap: space[2], marginBottom: space[6], flexWrap: "wrap" }}>
      {STEPS.map((step, i) => {
        const isActive = step.key === activeKey;
        const isDone = step.built && i < furthestIndex;
        const isReachable = step.built && i <= furthestIndex;
        return (
          <button
            key={step.key}
            type="button"
            aria-current={isActive ? "step" : undefined}
            disabled={!isReachable}
            title={step.built ? undefined : "Not built yet"}
            onClick={() => isReachable && onSelect(step.key)}
            style={{
              flex: "1 1 150px",
              textAlign: "left",
              background: color.bg.surface,
              border: `1px solid ${isActive ? color.brand.solid : color.border.subtle}`,
              boxShadow: isActive ? `inset 0 0 0 1px ${color.brand.solid}` : "none",
              borderRadius: radius.lg,
              padding: `${space[3]}px ${space[4]}px`,
              cursor: isReachable ? "pointer" : "not-allowed",
              opacity: step.built ? 1 : 0.45,
            }}
          >
            <div
              style={{
                fontSize: text.xs,
                fontWeight: weight.semibold,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: isDone ? TONES.success.fg : color.text.tertiary,
                marginBottom: 2,
              }}
            >
              {isDone ? "✓ " : ""}Step {i + 1}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: space[2], fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}>
              {step.label}
              {status[step.key] === "busy" && <Spinner size={12} label={`${step.label} in progress`} />}
              {status[step.key] === "error" && (
                <span title="Needs attention" aria-label="Needs attention" style={{ width: 8, height: 8, borderRadius: "50%", background: TONES.danger.solid }} />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

const STEP_KEYS = STEPS.map((s) => s.key);

/* ========================================================================
 * useScreeningRunner (formerly useScreeningRunner.js) — runs screening +
 * document verification for the whole page through the TFC backend (it talks
 * to the onboarding API server-side), so the work survives moving between
 * steps and is never resubmitted needlessly.
 *
 *   run-screening        merchant screening + every document's verification, once
 *   screening-status     one poll for the screening and every document verification
 *   retry-document-…     resubmits only documents that are new, changed or failed
 *
 * Everything is keyed by a FINGERPRINT of the exact request:
 *   • Screening: coming back to the Screening step with unchanged data never
 *     re-submits — the stored run is shown, and an in-flight run keeps polling.
 *   • Documents: each verification ("merchant" or an officer_id) is resubmitted
 *     only when its own payload changed (new file, new ID type).
 *
 * State lives in the page's `data` (screeningRun, onboarding, docRuns,
 * merchantVerification, officerVerifications, resultsVersion); the one poll
 * job is tracked in a ref so a double-mounted effect can't start two.
 * ========================================================================
 */
const RUNNING_PHASES = new Set(["submitting", "screening"]);

// A document request that never produced an application (pre-validation,
// linkage, network). Never sent to the report.
const notSubmitted = (err) => ({
  status: "NOT_SUBMITTED",
  decision: null,
  message: err?.message || "The document could not be submitted.",
  validation_results: err?.validation_results || err?.validationResults || null,
});

const bump = (s) => (s.resultsVersion || 0) + 1;
const docRecord = (s, k) => (k === "merchant" ? s.merchantVerification : s.officerVerifications?.[k]);

function withDocRecord(s, k, rec) {
  if (k === "merchant") return { ...s, merchantVerification: rec };
  const next = { ...s.officerVerifications };
  if (rec) next[k] = rec;
  else delete next[k];
  return { ...s, officerVerifications: next };
}

// Document targets without the screening link — the backend adds it.
const targetsOf = (d) => docTargets(d, null);

function useScreeningRunner(data, setData) {
  const dataRef = useRef(data);
  dataRef.current = data;
  const job = useRef(null); // { token } — the one status poll
  const screeningFp = useRef(null);
  // Verification application ids per target, updated as soon as they are
  // known (state updates land later than the next poll may start).
  const ids = useRef(null);

  const cancel = () => {
    if (job.current) job.current.token.cancelled = true;
    job.current = null;
  };
  const polling = () => !!job.current && !job.current.token.cancelled && !job.current.token.done;

  useEffect(() => () => cancel(), []);

  const patchRun = (token, patch) => {
    if (token.cancelled) return;
    setData((s) => ({ ...s, screeningRun: { ...s.screeningRun, ...patch } }));
  };

  // Records the results of submitting documents (run-screening / retry).
  function applySubmitted(results, payloads) {
    ids.current = { ...docIds() };
    for (const [k, res] of Object.entries(results || {})) {
      if (res && Number.isInteger(res.application_id)) ids.current[k] = res.application_id;
      else delete ids.current[k];
    }
    setData((s) => {
      let next = { ...s, docRuns: { ...s.docRuns } };
      let changed = false;
      for (const [k, res] of Object.entries(results || {})) {
        const fp = payloads[k] ? fingerprint(payloads[k]) : next.docRuns[k]?.fingerprint;
        if (res && Number.isInteger(res.application_id)) {
          next.docRuns[k] = { fingerprint: fp, applicationId: res.application_id };
          next = withDocRecord(next, k, { status: res.status || "RECEIVED", application_id: res.application_id });
        } else {
          next.docRuns[k] = { fingerprint: fp, applicationId: null };
          next = withDocRecord(next, k, notSubmitted(res?.error));
          changed = true;
        }
      }
      return changed ? { ...next, resultsVersion: bump(s) } : next;
    });
  }

  const docIds = () => ids.current || Object.fromEntries(
    Object.entries(dataRef.current.docRuns || {}).filter(([, r]) => r.applicationId).map(([k, r]) => [k, r.applicationId])
  );

  // Applies one screening-status response.
  function applyStatus(token, res) {
    if (token.cancelled || !res) return;
    setData((s) => {
      let next = { ...s };
      let changed = false;
      const rec = res.screening;
      if (rec) {
        if (isOnboardingTerminal(rec) && s.screeningRun?.phase !== "done") {
          next = {
            ...next,
            onboarding: rec,
            verificationKey: rec.verification_key || s.verificationKey,
            screeningRun: { ...s.screeningRun, latest: rec, phase: "done", verificationKey: rec.verification_key,
              retrying: null, error: null, finishedAt: Date.now() },
          };
          changed = true;
        } else if (!isOnboardingTerminal(rec)) {
          next = { ...next, screeningRun: { ...s.screeningRun, latest: rec, retrying: null } };
        }
      }
      for (const [k, doc] of Object.entries(res.documents || {})) {
        const prev = docRecord(s, k) || {};
        if (doc.poll_error) {
          next = withDocRecord(next, k, { ...prev, pollError: doc.poll_error });
          continue;
        }
        if (isDocVerificationTerminal(doc) && !isDocVerificationTerminal(prev)) changed = true;
        next = withDocRecord(next, k, doc);
      }
      return changed ? { ...next, resultsVersion: bump(s) } : next;
    });
  }

  // Done when the screening and every submitted document have a final result.
  function allDone(res) {
    if (!res || !isOnboardingTerminal(res.screening)) return false;
    const ids = docIds();
    return Object.keys(ids).every((k) => {
      const doc = res.documents?.[k];
      return !doc || doc.poll_error || isDocVerificationTerminal(doc);
    });
  }

  async function poll(applicationId) {
    cancel();
    const token = { cancelled: false, done: false };
    job.current = { token };
    try {
      const final = await pollUntilTerminal(() => getScreeningStatus(applicationId, docIds()), applicationId, allDone, {
        timeoutMs: SCREENING_TIMEOUT_MS,
        signal: token,
        onUpdate: (res) => applyStatus(token, res),
        onRetry: (e) => patchRun(token, { retrying: e.message }),
      });
      if (token.cancelled) return;
      token.done = true;
      applyStatus(token, final);
    } catch (e) {
      if (e.cancelled || token.cancelled) return;
      token.done = true;
      const done = dataRef.current.screeningRun?.phase === "done";
      if (e.timeout && done) {
        // Screening finished; mark documents still in flight as timed out.
        setData((s) => {
          let next = s;
          for (const k of Object.keys(s.docRuns || {})) {
            const rec = docRecord(s, k);
            if (rec && !isDocVerificationTerminal(rec) && rec.status !== "NOT_SUBMITTED") next = withDocRecord(next, k, { ...rec, timedOut: true });
          }
          return next;
        });
      } else if (!done) {
        patchRun(token, e.timeout
          ? { phase: "timeout", error: e.message, latest: e.lastResult?.screening || null, retrying: null }
          : { phase: "failed", error: e.message, errorAt: "poll", retrying: null });
      }
    }
  }

  function startScreening() {
    const d = dataRef.current;
    const payload = buildScreeningPayload(d);
    const fp = fingerprint(payload);
    const docs = targetsOf(d);
    cancel();
    screeningFp.current = fp;
    ids.current = {};
    const token = { cancelled: false, done: false };
    job.current = { token };

    setData((s) => ({
      ...s,
      screeningRun: {
        fingerprint: fp, phase: "submitting", applicationId: null, verificationKey: null, latest: null,
        error: null, errorAt: null, retrying: null, startedAt: Date.now(), finishedAt: null,
      },
      onboarding: null,
      verificationKey: null,
      merchantVerification: docs.merchant ? { status: "SUBMITTING" } : null,
      officerVerifications: Object.fromEntries(Object.keys(docs).filter((k) => k !== "merchant").map((k) => [k, { status: "SUBMITTING" }])),
      docRuns: {},
      resultsVersion: bump(s),
    }));

    (async () => {
      let ack;
      try {
        ack = await runScreening(payload, docs);
      } catch (e) {
        if (token.cancelled) return;
        token.done = true;
        setData((s) => ({ ...s, merchantVerification: null, officerVerifications: {} }));
        patchRun(token, { phase: "failed", error: e.message, errorAt: "submit" });
        return;
      }
      if (token.cancelled) return;
      setData((s) => ({
        ...s,
        verificationKey: ack.verification_key || null,
        screeningRun: { ...s.screeningRun, applicationId: ack.application_id, verificationKey: ack.verification_key || null,
          latest: ack, phase: "screening" },
      }));
      applySubmitted(ack.documents, docs);
      if (!token.cancelled) poll(ack.application_id);
    })();
  }

  // "Check again" after a timeout / lost connection: polls the SAME
  // application — never re-submits.
  function resumeScreening() {
    const run = dataRef.current.screeningRun;
    if (!run || !run.applicationId) return startScreening();
    patchRun({ cancelled: false }, { phase: run.phase === "done" ? "done" : "screening", error: null, errorAt: null, retrying: null });
    poll(run.applicationId);
  }

  // Resubmits the given document targets to the same screening.
  async function resubmit(targets) {
    const run = dataRef.current.screeningRun;
    if (!run?.applicationId || !Object.keys(targets).length) return;
    setData((s) => Object.keys(targets).reduce((acc, k) => withDocRecord(acc, k, { status: "SUBMITTING" }), s));
    try {
      const out = await retryDocumentVerification(run.applicationId, targets);
      applySubmitted(out.documents, targets);
    } catch (e) {
      applySubmitted(Object.fromEntries(Object.keys(targets).map((k) => [k, { error: { message: e.message } }])), targets);
      return;
    }
    if (!polling()) poll(run.applicationId);
  }

  // Submits documents that are new or changed; drops results for removed ones.
  function syncDocs() {
    const d = dataRef.current;
    const targets = targetsOf(d);
    const known = d.docRuns || {};
    for (const k of Object.keys(known)) {
      if (k in targets) continue;
      if (ids.current) delete ids.current[k];
      setData((s) => {
        const runs = { ...s.docRuns };
        delete runs[k];
        return { ...withDocRecord(s, k, null), docRuns: runs };
      });
    }
    const changed = Object.fromEntries(Object.entries(targets).filter(([k, p]) => known[k]?.fingerprint !== fingerprint(p)));
    resubmit(changed);
  }

  // Resubmit one document (after "not submitted" / "could not verify").
  function retryDoc(k) {
    const payload = targetsOf(dataRef.current)[k];
    if (payload) resubmit({ [k]: payload });
  }

  // Poll again — no resubmission.
  function checkDocAgain() {
    const run = dataRef.current.screeningRun;
    if (run?.applicationId) poll(run.applicationId);
  }

  // Called when the Screening step opens. Starts a run only when there is
  // none yet or the confirmed data changed since the last one.
  function ensure() {
    const d = dataRef.current;
    const fp = fingerprint(buildScreeningPayload(d));
    const knownFp = screeningFp.current || d.screeningRun?.fingerprint;
    if (knownFp !== fp) {
      startScreening();
      return;
    }
    const run = d.screeningRun;
    if (!run?.applicationId) return;
    syncDocs();
    const docsInFlight = Object.keys(d.docRuns || {}).some((k) => {
      const rec = docRecord(d, k);
      return rec && !isDocVerificationTerminal(rec) && rec.status !== "NOT_SUBMITTED" && !rec.timedOut;
    });
    if ((run.phase === "screening" || docsInFlight) && !polling()) poll(run.applicationId);
  }

  // Abandon everything in flight (used when starting a new onboarding).
  function reset() {
    cancel();
    screeningFp.current = null;
    ids.current = null;
  }

  return { ensure, rerun: startScreening, checkAgain: resumeScreening, retryDoc, checkDocAgain, reset };
}

/* ========================================================================
 * UploadStep (formerly UploadStep.jsx)
 * ========================================================================
 */
// The formation document (any type in FORMATION_DOC_TYPES) is the only
// business document /v1/verification/documents authenticates (state seal +
// Secretary of State signature). It always goes in this API slot. Optional.
const COI_SLOT = "certificate_of_incorporation";

// The analyst's choice, else a default from the entity type on the intake form.
const effectiveFormationType = (data) =>
  data.formationDocType || suggestedFormationDocType(data.merchant.business_type);

const toDoc = (r) => ({ storage_uri: r.storage_uri, file_name: r.file_name, content_type: r.content_type, size: r.size });

// `onNewOnboarding(intakeFile)` resets the whole page for a new intake form:
// a new form is a new applicant, so nothing from the previous one (details,
// owners, documents, screening, report) may carry over.
// `onUploadsChanged()` is called whenever a document, ID type or formation
// type changes, so the page can make the analyst pass Step 1's checks again.
function UploadStep({ data, setData, onNext, onNewOnboarding, onUploadsChanged }) {
  const [uploadState, setUploadState] = useState({}); // key -> { uploading, error }
  const [parseState, setParseState] = useState({ running: false, error: null });
  const [formError, setFormError] = useState(null);
  const errorRef = useRef(null);
  // Latest request per slot wins — a slow earlier upload can't overwrite a
  // newer file dropped into the same slot.
  const latest = useRef({});
  // Latest intake form wins — a slow parse of an earlier form is discarded.
  const parseTicket = useRef(null);

  // Any document change invalidates the confirmed checklist and Step 1's checks.
  const uploadsChanged = () => {
    setData((s) => (s.checklistConfirmed ? { ...s, checklistConfirmed: false } : s));
    if (onUploadsChanged) onUploadsChanged();
  };

  const track = (key, value) => setUploadState((s) => ({ ...s, [key]: value }));

  // Validates, uploads, then hands the stored-file record to `apply`.
  async function uploadInto(key, file, rule, apply) {
    const problem = checkFile(file, rule);
    if (problem) {
      track(key, { uploading: false, error: problem });
      return null;
    }
    const ticket = {};
    latest.current[key] = ticket;
    track(key, { uploading: true, error: null });
    try {
      const result = await uploadDocument(file);
      if (latest.current[key] !== ticket) return null;
      apply(toDoc(result));
      track(key, { uploading: false, error: null });
      return result;
    } catch (e) {
      if (latest.current[key] !== ticket) return null;
      track(key, { uploading: false, error: `Upload failed: ${e.message}` });
      return null;
    }
  }

  // Parsing starts as soon as the form lands, so the officer list below is
  // built from the form (names, ID type) before any ID is attached — an
  // uploaded ID is then always attributed to a named person. Sourced from
  // onboarding_intake's prefill() (canonical keys), converted to the shape
  // the rest of this wizard already expects so everything downstream —
  // Confirm, Screening, Report — is unchanged.
  async function runParse(file) {
    const ticket = {};
    parseTicket.current = ticket;
    setParseState({ running: true, error: null });
    try {
      const prefilled = await intakePrefill(file);
      if (parseTicket.current !== ticket) return;
      const parsed = canonicalToLegacyIntake(prefilled.values || {}, prefilled.warnings);
      setData((s) => ({
        ...s,
        intakeFormParsed: parsed,
        checklistOverrides: {}, // a new form re-derives every checklist item
        merchant: mergeParsedMerchant(s.merchant, parsed),
        officers: mergeExtractedOfficers(s.officers, parsed.officers),
      }));
      setParseState({ running: false, error: null });
    } catch (e) {
      if (parseTicket.current !== ticket) return;
      setParseState({ running: false, error: e.message });
    }
  }

  async function handleIntakeFormFiles(files) {
    const hasWork = !!(data.intakeFormFile || data.screeningRun || data.officers.length);
    if (hasWork && !window.confirm(
      "Uploading a new intake form starts a new onboarding. The current details, documents, screening results " +
      "and report on this page will be cleared. Continue?"
    )) return;
    // The form is only read — parsed from the file in memory, never uploaded
    // or stored. The File is kept on the page for Retry.
    const file = files[0];
    const problem = checkFile(file, FILE_RULES.intake);
    if (problem) {
      track("intakeForm", { uploading: false, error: problem });
      return;
    }
    const doc = { file_name: file.name, size: file.size, content_type: file.type, file };
    latest.current = {}; // uploads still in flight belong to the previous applicant
    setUploadState({});
    setFormError(null);
    if (onNewOnboarding) onNewOnboarding(doc);
    else setData((s) => ({ ...s, intakeFormFile: doc, intakeFormParsed: null }));
    await runParse(file);
  }

  const setMerchantDoc = (slotKey, doc) => {
    setData((s) => ({
      ...s,
      merchant: { ...s.merchant, documents: { ...s.merchant.documents, [slotKey]: doc } },
    }));
    uploadsChanged();
  };

  const updateOfficer = (officerId, patch) => {
    setData((s) => ({
      ...s,
      officers: s.officers.map((o) => (o.officer_id !== officerId ? o : { ...o, ...patch })),
    }));
    uploadsChanged();
  };

  const setOfficerDoc = (officerId, slot, doc) => {
    setData((s) => ({
      ...s,
      officers: s.officers.map((o) =>
        o.officer_id !== officerId ? o : { ...o, documents: { ...o.documents, [slot]: doc } }
      ),
    }));
    uploadsChanged();
  };

  function validate() {
    if (!data.intakeFormParsed && !parseState.running) return "Upload the intake form — business and owner details come only from it.";
    if (uploadState.intakeForm?.uploading || parseState.running) return "Wait for the intake form to finish reading.";
    if (Object.values(uploadState).some((u) => u?.uploading)) return "Wait for the uploads to finish.";
    if (data.merchant.documents[COI_SLOT]?.storage_uri && !effectiveFormationType(data)) {
      return "Choose the type of formation document you uploaded.";
    }
    if (!data.checklistConfirmed) return "Confirm the document checklist before continuing.";
    for (const o of data.officers) {
      const who = o.full_name || "A person with no name on the intake form";
      const hasId = !!o.documents.id?.storage_uri;
      const hasSelfie = !!o.documents.selfie?.storage_uri;
      if (hasId && !o.id_doc_type) return `${who}: choose the ID document type.`;
      if (hasId !== hasSelfie) return `${who}: an ID document needs a matching live selfie, and vice versa.`;
      if (hasId && isTwoSided(o.id_doc_type) && !o.documents.id_back?.storage_uri) {
        const typeLabel = (ID_DOC_TYPES.find((t) => t.value === o.id_doc_type)?.label || "ID document").toLowerCase();
        return `${who}: a ${typeLabel} is two-sided — upload the back as well.`;
      }
      if (hasId && !o.full_name.trim()) return "Every person with an ID uploaded needs a full legal name on the intake form.";
    }
    return null;
  }

  function handleContinue() {
    const err = validate();
    setFormError(err);
    if (err) {
      setTimeout(() => errorRef.current && errorRef.current.scrollIntoView && errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    // Freeze the checklist the analyst confirmed (and the formation document
    // type shown) — this is what the report shows.
    setData((s) => ({ ...s, formationDocType: effectiveFormationType(s), docChecklist: effectiveChecklist(s) }));
    onNext();
  }

  const parsed = data.intakeFormParsed;
  const coi = data.merchant.documents[COI_SLOT];
  const formationType = effectiveFormationType(data);
  const formationLabel = formationDocLabel(formationType);
  const auto = deriveChecklist(data);
  const checklist = effectiveChecklist(data, auto);
  const missingC = missingCounts(checklist);
  const badge = !missingC.required && !missingC.recommended
    ? { tone: "success", text: "Complete" }
    : {
        tone: missingC.required ? "danger" : "warning",
        text: [missingC.required && `${missingC.required} required missing`, missingC.recommended && `${missingC.recommended} recommended`]
          .filter(Boolean).join(" · "),
      };
  const idsUploaded = data.officers.filter((o) => o.documents.id?.storage_uri && o.documents.selfie?.storage_uri).length;
  const formProblems = (parsed?.form_checks || []).filter((c) => c.severity !== "info");
  const anyUploading = parseState.running || Object.values(uploadState).some((u) => u?.uploading);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
      <Card header={{
        title: "Intake form",
        right: parseState.running ? <StatusBadge tone="info" busy>Reading</StatusBadge>
          : parsed ? <StatusBadge tone="success">Read</StatusBadge>
          : parseState.error ? <StatusBadge tone="warning">Not read</StatusBadge> : null,
      }}>
        <Dropzone
          label="Drop the completed CDD intake form"
          hint={FILE_RULES.intake.label}
          accept={FILE_RULES.intake.accept}
          uploaded={data.intakeFormFile}
          uploading={parseState.running}
          doneLabel="Selected"
          error={uploadState.intakeForm?.error}
          onFiles={handleIntakeFormFiles}
        />
        {parseState.running && (
          <div style={{ marginTop: space[3], display: "flex", alignItems: "center", gap: space[2], fontSize: text.base, color: color.text.secondary }}>
            <Spinner size={14} /> Reading the form — owners, addresses and declarations…
          </div>
        )}
        {parseState.error && (
          <div style={{ marginTop: space[3] }}>
            <Notice
              tone="warning"
              title="Couldn't read this form automatically"
              actions={data.intakeFormFile?.file && <Button size="sm" onClick={() => runParse(data.intakeFormFile.file)}>Retry</Button>}
            >
              {parseState.error} Check the file and try again, or upload a corrected intake form.
            </Notice>
          </div>
        )}
        {parsed && (
          <div style={{ marginTop: space[3], fontSize: text.base, color: color.text.secondary }}>
            <strong>{parsed.legal_business_name || "Business name missing"}</strong>
            {parsed.reference_id ? ` · ref ${parsed.reference_id}` : ""} · {parsed.officers?.length || 0} owners and control
            persons · {parsed.page_count} pages
            {formProblems.length > 0 && (
              <span style={{ color: TONES.warning.fg }}>
                {" "}· {formProblems.length} issue{formProblems.length === 1 ? "" : "s"} to review in the next step
              </span>
            )}
          </div>
        )}
        {parsed && parsed.form_checks?.length > 0 && (
          <div style={{ marginTop: space[3] }}>
            <Notice tone={formProblems.length > 0 ? "warning" : "info"} title="Problems found reading the document">
              <ul style={{ margin: 0, paddingLeft: space[4] }}>
                {parsed.form_checks.map((c, i) => (
                  <li key={i}>{c.message}</li>
                ))}
              </ul>
            </Notice>
          </div>
        )}
      </Card>

      <Accordion
        title="Formation document"
        subtitle="Optional"
        summary={coi ? `${formationLabel || "Formation document"} uploaded` : "Not uploaded"}
      >
        <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[3] }}>
          The state filing that created the entity — authenticated for US state filings (state seal and Secretary of
          State signature). Choose its type, then upload it.
        </div>
        <div style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: space[3] }}>
          <Field label="Document type" required={!!coi}>
            <Select
              value={formationType}
              onChange={(e) => { setData((s) => ({ ...s, formationDocType: e.target.value })); uploadsChanged(); }}
            >
              <option value="">Select the formation document…</option>
              {FORMATION_DOC_TYPES.map((g) => (
                <optgroup key={g.entity} label={g.entity}>
                  {g.types.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </Field>
          <Dropzone
            label="Upload the formation document"
            hint={FILE_RULES.business.label}
            accept={FILE_RULES.business.accept}
            uploaded={coi}
            uploading={uploadState[COI_SLOT]?.uploading}
            error={uploadState[COI_SLOT]?.error}
            onFiles={(files) => uploadInto(COI_SLOT, files[0], FILE_RULES.business, (doc) => setMerchantDoc(COI_SLOT, doc))}
            onRemove={() => setMerchantDoc(COI_SLOT, null)}
          />
        </div>
      </Accordion>

      {/* Only once the intake form has listed people. */}
      {data.officers.length > 0 && (
        <Accordion
          title="ID and selfie"
          subtitle="Optional"
          summary={`${idsUploaded} of ${data.officers.length} with ID + selfie`}
        >
          <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[3] }}>
            A government-issued ID and a live selfie for each owner or control person you want identity-verified.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
          {data.officers.map((o) => {
            const twoSided = isTwoSided(o.id_doc_type);
            const k = (slot) => `${o.officer_id}:${slot}`;
            return (
              <div key={o.officer_id} style={{ border: `1px solid ${color.border.subtle}`, borderRadius: 8, padding: space[4] }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space[3], gap: space[3] }}>
                  <div style={{ display: "flex", gap: space[2], alignItems: "center" }}>
                    <span style={{ fontWeight: weight.semibold, fontSize: text.md }}>
                      {o.full_name || <span style={{ color: TONES.danger.fg }}>Name missing</span>}
                    </span>
                    {o.role && <span style={{ fontSize: text.sm, color: color.text.tertiary }}>{o.role}</span>}
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 320px)", gap: space[3] }}>
                  <Field
                    label="ID document type"
                    hint={!o.id_doc_type ? "Choose the type of ID being uploaded" : twoSided ? "Two-sided — front and back needed" : "Single page"}
                  >
                    <Select value={o.id_doc_type} onChange={(e) => updateOfficer(o.officer_id, { id_doc_type: e.target.value })}>
                      <option value="">Select the ID type…</option>
                      {ID_DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                  </Field>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: twoSided ? "1fr 1fr 1fr" : "1fr 1fr", gap: space[3], marginTop: space[3] }}>
                  <Dropzone
                    label={twoSided ? "ID — front" : "ID document"}
                    hint={FILE_RULES.id.label}
                    accept={FILE_RULES.id.accept}
                    uploaded={o.documents.id}
                    uploading={uploadState[k("id")]?.uploading}
                    error={uploadState[k("id")]?.error}
                    onFiles={(files) => uploadInto(k("id"), files[0], FILE_RULES.id, (doc) => setOfficerDoc(o.officer_id, "id", doc))}
                    onRemove={() => setOfficerDoc(o.officer_id, "id", null)}
                  />
                  {twoSided && (
                    <Dropzone
                      label="ID — back"
                      hint={FILE_RULES.id.label}
                      accept={FILE_RULES.id.accept}
                      uploaded={o.documents.id_back}
                      uploading={uploadState[k("id_back")]?.uploading}
                      error={uploadState[k("id_back")]?.error}
                      onFiles={(files) => uploadInto(k("id_back"), files[0], FILE_RULES.id, (doc) => setOfficerDoc(o.officer_id, "id_back", doc))}
                      onRemove={() => setOfficerDoc(o.officer_id, "id_back", null)}
                    />
                  )}
                  <Dropzone
                    label="Live selfie"
                    hint={FILE_RULES.selfie.label}
                    accept={FILE_RULES.selfie.accept}
                    uploaded={o.documents.selfie}
                    uploading={uploadState[k("selfie")]?.uploading}
                    error={uploadState[k("selfie")]?.error}
                    onFiles={(files) => uploadInto(k("selfie"), files[0], FILE_RULES.selfie, (doc) => setOfficerDoc(o.officer_id, "selfie", doc))}
                    onRemove={() => setOfficerDoc(o.officer_id, "selfie", null)}
                  />
                </div>
              </div>
            );
          })}
          </div>
        </Accordion>
      )}

      <Card
        header={{
          title: "Submission checklist",
          right: parsed ? <StatusBadge tone={badge.tone}>{badge.text}</StatusBadge> : null,
        }}
      >
        <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[3] }}>
          {parsed
            ? "Filled in from the intake form and your uploads. To change an item, correct the intake form or the uploads."
            : "Filled in once the intake form is read."}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
          {CHECKLIST_GROUPS.map((group) => (
            <div key={group.title}>
              <div style={{ fontSize: text.sm, fontWeight: weight.semibold, color: color.text.primary }}>
                {group.title}
                <span style={{ marginLeft: space[2], fontSize: text.xs, fontWeight: weight.medium, color: group.required ? TONES.danger.fg : color.text.tertiary }}>
                  {group.required ? "Required" : "Recommended"}
                </span>
                <span style={{ marginLeft: space[2], fontWeight: weight.regular, color: color.text.tertiary }}>{group.note}</span>
              </div>
              {group.items.map(({ label, hint }) => {
                const on = !!checklist[label];
                const { status, detail } = auto[label] || {};
                const missingColor = group.required ? TONES.danger.fg : color.text.tertiary;
                const tag = status === "upload" ? { text: "Uploaded", c: TONES.success.fg }
                  : status === "form" ? { text: "Provided", c: TONES.success.fg }
                  : status === "declared" ? { text: "Declared, not uploaded", c: TONES.warning.fg }
                  : parsed ? { text: "Missing", c: missingColor } : null;
                const dot = !parsed ? TONES.neutral.solid : on ? TONES.success.solid : status === "declared" ? TONES.warning.solid
                  : group.required ? TONES.danger.solid : TONES.neutral.solid;
                return (
                  <div key={label} style={{ display: "flex", alignItems: "flex-start", gap: space[2], padding: `${space[1]}px 0`, fontSize: text.base }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, marginTop: 6, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      {label}
                      {hint && (
                        <span style={{ display: "block", fontSize: text.xs, color: color.text.tertiary }}>{hint}</span>
                      )}
                      {parsed && detail && (
                        <span style={{ display: "block", fontSize: text.xs, color: on ? color.text.tertiary : tag?.c || color.text.tertiary }}>
                          {detail}
                        </span>
                      )}
                    </span>
                    {tag && <span style={{ fontSize: text.xs, color: tag.c, whiteSpace: "nowrap" }}>{tag.text}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <label
          style={{
            display: "flex", alignItems: "center", gap: space[2], marginTop: space[4], paddingTop: space[3],
            borderTop: `1px solid ${color.border.subtle}`, fontSize: text.base, fontWeight: weight.medium,
          }}
        >
          <input
            type="checkbox"
            checked={!!data.checklistConfirmed}
            onChange={(e) => setData((s) => ({ ...s, checklistConfirmed: e.target.checked }))}
          />
          I confirm this checklist is accurate and complete.
        </label>
      </Card>

      {formError && (
        <div ref={errorRef}>
          <Notice tone="danger" title="Fix this before continuing">{formError}</Notice>
        </div>
      )}

      <div style={{ display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="primary" disabled={!data.checklistConfirmed || anyUploading} onClick={handleContinue}>
          Continue to confirm reading
        </Button>
        {(anyUploading || !data.checklistConfirmed) && (
          <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
            {anyUploading ? "Waiting for uploads to finish…" : "Confirm the checklist to continue."}
          </span>
        )}
      </div>
    </div>
  );
}

/* ========================================================================
 * ConfirmReadingStep (formerly ConfirmReadingStep.jsx)
 * ========================================================================
 */
const COUNTRIES = [
  ["US", "United States"], ["MX", "Mexico"], ["CA", "Canada"], ["FR", "France"], ["GB", "United Kingdom"],
  ["CO", "Colombia"], ["SG", "Singapore"], ["DE", "Germany"], ["IN", "India"], ["BR", "Brazil"],
  ["ES", "Spain"], ["IT", "Italy"], ["NL", "Netherlands"], ["CH", "Switzerland"], ["AE", "United Arab Emirates"],
  ["CN", "China"], ["HK", "Hong Kong"], ["JP", "Japan"], ["AU", "Australia"], ["IL", "Israel"],
  ["NG", "Nigeria"], ["ZA", "South Africa"], ["PH", "Philippines"], ["AR", "Argentina"], ["CL", "Chile"],
  ["PE", "Peru"], ["PA", "Panama"], ["RU", "Russia"], ["IQ", "Iraq"], ["IR", "Iran"],
];

const CHECK_TONE = { error: "danger", warning: "warning", info: "info" };
// Same severity scale as the report (High / Medium / Low), shown above the checks.
const CHECK_LEGEND = [
  ["error", "High — must be resolved before onboarding"],
  ["warning", "Medium — needs review"],
  ["info", "Low — for information"],
];
const CHECK_RANK = { error: 0, warning: 1, info: 2 };
const CHECK_LABEL = { error: "High", warning: "Medium", info: "Low" };
const yn = (v) => (v === true ? "Yes" : v === false ? "No" : undefined);

// Read-only: every value on this step comes from the intake form. To change
// one, the analyst corrects the form and uploads it again in Step 1.
// Country name alone (table cells), e.g. "United States".
const countryLabel = (code) => {
  if (!code) return "";
  const hit = COUNTRIES.find(([c]) => c === String(code).toUpperCase());
  return hit ? hit[1] : code;
};
const countryName = (code) => {
  if (!code) return "";
  const hit = COUNTRIES.find(([c]) => c === String(code).toUpperCase());
  return hit ? `${hit[0]} — ${hit[1]}` : code;
};

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";

// Plain value: "—" when empty, red "Not on the form" when the value is required.
function Val({ value, required }) {
  if (isBlank(value)) {
    return <span style={{ color: required ? TONES.danger.fg : color.text.tertiary }}>{required ? "Missing" : "—"}</span>;
  }
  return <span>{value}</span>;
}

// One label/value pair in a read-only details grid.
function Item({ label, value, required, note, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <div style={{ fontSize: text.xs, color: color.text.tertiary, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: text.base, color: color.text.primary, wordBreak: "break-word" }}>
        <Val value={value} required={required} />
      </div>
      {note && <div style={{ fontSize: text.xs, color: color.text.tertiary, marginTop: 2 }}>{note}</div>}
    </div>
  );
}

function formatAddress(a) {
  if (!a || addressIsEmpty(a)) return "";
  return [a.line1, a.line2, a.city, [a.state, a.postal_code].filter(Boolean).join(" "), countryLabel(a.country)]
    .filter((p) => p && String(p).trim()).join(", ");
}

const KEY_CHIP = {
  fontSize: text.xs, fontWeight: weight.semibold, color: color.brand.solid, background: color.brand.subtle,
  border: `1px solid ${color.brand.border}`, borderRadius: radius.sm, padding: "1px 8px", whiteSpace: "nowrap",
};

const TH = {
  textAlign: "left", fontSize: text.xs, fontWeight: weight.medium, color: color.text.tertiary,
  padding: `${space[2]}px ${space[3]}px`, borderBottom: `1px solid ${color.border.subtle}`, whiteSpace: "nowrap",
};
const TD = {
  fontSize: text.base, color: color.text.primary, padding: `${space[2]}px ${space[3]}px`,
  borderBottom: `1px solid ${color.border.subtle}`, verticalAlign: "top",
};

function ConfirmReadingStep({ data, setData, onNext, onBack }) {
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  // The confirmation is tied to the exact values confirmed: it survives going
  // back and forth, and clears itself as soon as any screened value changes.
  const currentFp = screeningFingerprint(data);
  const confirmed = data.readingConfirmedFp === currentFp;
  const setConfirmed = (on) => setData((s) => ({ ...s, readingConfirmedFp: on ? screeningFingerprint(s) : null }));
  const [formError, setFormError] = useState(null);
  const autoRunStarted = useRef(false);
  const errorRef = useRef(null);

  const merchant = data.merchant;
  const ifp = data.intakeFormParsed;

  // Upload normally parses the form already; this covers a failed / skipped
  // parse (e.g. the user moved on before it finished).
  async function runParse() {
    if (!data.intakeFormFile?.file) return;
    setParsing(true);
    setParseError(null);
    try {
      const prefilled = await intakePrefill(data.intakeFormFile.file);
      const parsed = canonicalToLegacyIntake(prefilled.values || {}, prefilled.warnings);
      setData((s) => ({
        ...s,
        intakeFormParsed: parsed,
        merchant: mergeParsedMerchant(s.merchant, parsed),
        officers: mergeExtractedOfficers(s.officers, parsed.officers),
      }));
    } catch (e) {
      setParseError(e.message);
    } finally {
      setParsing(false);
    }
  }

  useEffect(() => {
    if (!ifp && data.intakeFormFile && !autoRunStarted.current) {
      autoRunStarted.current = true;
      runParse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Everything comes from the intake form, so a gap is fixed on the form.
  // Block only on what the onboarding API rejects or screening can't run
  // without; other gaps (address lines, role, nationality) stay visible as
  // "Missing" and in the report, but don't stop screening.
  function validate() {
    const fix = "Correct the intake form and upload it again in Step 1.";
    if (!ifp) return "Upload the intake form in Step 1.";
    if (!merchant.legal_business_name.trim()) return `Legal business name is missing. ${fix}`;
    const inc = resolveIncorporation(merchant);
    if (!inc.country) return `Country of incorporation is missing. ${fix}`;
    // The business registry check can't run for a US company without its state.
    if (inc.country.toUpperCase() === "US" && !inc.state) return `State of incorporation is missing. ${fix}`;
    for (const o of data.officers) {
      if (!o.full_name || !o.full_name.trim()) return `A person has no full legal name. ${fix}`;
      if (isBlank(o.ownership_percentage)) return `${o.full_name}: ownership % is missing. ${fix}`;
      const pct = Number(o.ownership_percentage);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) return `${o.full_name}: ownership must be between 0 and 100%. ${fix}`;
      if (!o.dob) return `${o.full_name}: date of birth is missing. ${fix}`;
    }
    return null;
  }

  function handleContinue() {
    if (!confirmed) return;
    const err = validate();
    setFormError(err);
    if (err) {
      // Make sure the reason is on screen.
      setTimeout(() => errorRef.current && errorRef.current.scrollIntoView && errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return;
    }
    // No clearing here: the Screening step compares what would be submitted
    // with the last run and only resubmits when something actually changed.
    onNext();
  }

  const current = !!data.screeningRun && data.screeningRun.fingerprint === currentFp;
  const continueLabel = !data.screeningRun ? "Run screening" : current ? "Continue to screening" : "Run screening again";

  const checks = ifp?.form_checks || [];
  const sortedChecks = [...checks].sort((a, b) => (CHECK_RANK[a.severity] ?? 3) - (CHECK_RANK[b.severity] ?? 3));
  const business = ifp?.business || {};
  const yesDisclosures = (ifp?.disclosures || []).filter((d) => d.answer === "YES");
  const keyCount = data.officers.filter((o) => keyBasis(o.role, o.ownership_percentage).length).length;
  const ownershipTotal = data.officers.reduce((t, o) => t + (Number(o.ownership_percentage) || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
      {parsing && (
        <Notice tone="info" title="Reading the intake form…" busy>This usually takes a few seconds.</Notice>
      )}
      {parseError && (
        <Notice tone="warning" title="Couldn't read the intake form"
          actions={<Button size="sm" onClick={runParse} disabled={parsing}>Retry</Button>}>
          {parseError} Check the file, or upload a corrected intake form in Step 1.
        </Notice>
      )}
      {!data.intakeFormFile && (
        <Notice tone="warning" title="No intake form uploaded">
          Upload the intake form in Step 1 to see the business and owner details.
        </Notice>
      )}
      {data.screeningRun && !current && (
        <Notice tone="warning" title="Intake form changed since the last screening">
          Continuing submits a new screening with the current details.
        </Notice>
      )}

      {ifp && (
        <Card header={{ title: "Intake form summary", right: <StatusBadge tone="neutral">{ifp.page_count} pages</StatusBadge> }}>
          <KeyValueBlock
            data={{
              "Requesting institution": ifp.request?.requesting_institution,
              "Reason for review": ifp.request?.reason_for_review,
              "Reference": ifp.reference_id,
              "Legal name / DBA": [ifp.legal_business_name, ifp.dba].filter(Boolean).join(" — "),
              "Entity type / formed": [ifp.business_type, business.formation_date].filter(Boolean).join(", "),
              "EIN / TIN": ifp.tax_id,
              "Nature of business": business.nature_of_business,
              "Licences": business.licenses,
              "MSB / virtual assets / payment processor": [yn(business.is_msb), yn(business.virtual_assets), yn(business.payment_processor)].join(" / "),
              "Counterparty countries": [...new Set((ifp.cross_border?.counterparties || []).map((c) => c.country).filter(Boolean))].join(", "),
              "Disclosures answered Yes": yesDisclosures.map((d) => `Q${d.number}`).join(", ") || "None",
              "Signed": ifp.certification?.signed ? `${ifp.certification.signer_name_title} — ${ifp.certification.date || "undated"}` : "Not signed",
            }}
          />
          {checks.length > 0 && (
            <div style={{ marginTop: space[4] }}>
              <div style={{ fontSize: text.sm, fontWeight: weight.medium, color: color.text.muted, marginBottom: space[2] }}>
                Integrity checks — carried into the report
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: `${space[1]}px ${space[4]}px`, fontSize: text.xs, color: color.text.tertiary, marginBottom: space[3] }}>
                {CHECK_LEGEND.map(([sev, meaning]) => (
                  <span key={sev} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: TONES[CHECK_TONE[sev]].solid }} />
                    {meaning}
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: space[2] }}>
                {sortedChecks.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: space[2], alignItems: "flex-start", fontSize: text.base }}>
                    <StatusBadge tone={CHECK_TONE[c.severity] || "neutral"}>{CHECK_LABEL[c.severity] || c.severity}</StatusBadge>
                    <span style={{ flex: 1 }}>{c.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card header={{ title: "Business details" }}>
        <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[4] }}>
          These are the details that will be screened. To correct anything, update the intake form and upload it
          again in Step 1.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: `${space[4]}px ${space[4]}px` }}>
          <Item label="Legal business name" value={merchant.legal_business_name} required span={2} />
          <Item label="Business type" value={merchant.business_type} />
          <Item label="Reference" value={merchant.reference_id} />
          <Item label="Tax ID / EIN" value={merchant.tax_id} />
          <Item label="Country of incorporation" value={countryName(resolveIncorporation(merchant).country)} required />
          <Item label="State of incorporation" value={resolveIncorporation(merchant).state} />
          <div />
          <Item label="Registered address" value={formatAddress(merchant.incorporation.registered_address)} required span={2} />
          <Item
            label="Physical operating address"
            value={formatAddress(merchant.operating_address)}
            note={addressIsEmpty(merchant.operating_address) ? "Not provided — the registered address is used" : undefined}
            span={2}
          />
        </div>
      </Card>

      <Card header={{ title: "Owners and control persons", right: (
        <div style={{ display: "flex", gap: space[2] }}>
          <StatusBadge tone="neutral">{keyCount} key {keyCount === 1 ? "person" : "persons"}</StatusBadge>
          <StatusBadge tone={ownershipTotal > 100 ? "danger" : "neutral"}>Ownership total {ownershipTotal}%</StatusBadge>
        </div>
      ) }}>
        <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[3] }}>
          Key persons are the CEO, the President and every owner with 25% or more. They, and the business, are what
          notes and recommended actions are tagged to.
        </div>
        {data.officers.length === 0 ? (
          <div style={{ fontSize: text.base, color: color.text.tertiary }}>
            No owners or control persons found. Correct the intake form and upload it again in Step 1.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH}>Name & role</th>
                  <th style={TH}>Key person</th>
                  <th style={{ ...TH, textAlign: "right" }}>Ownership</th>
                  <th style={TH}>Date of birth</th>
                  <th style={TH}>Nationality</th>
                  <th style={TH}>Residence</th>
                  <th style={TH}>PEP</th>
                  <th style={TH}>ID + selfie</th>
                </tr>
              </thead>
              <tbody>
                {data.officers.map((o) => {
                  const hasId = !!(o.documents.id?.storage_uri && o.documents.selfie?.storage_uri);
                  const basis = keyBasis(o.role, o.ownership_percentage);
                  const notes = [
                    !o.has_detail_block && "Individual details section missing",
                    (o.nationalities || []).length > 1 && `Citizenship: ${o.citizenship_raw} — first screened, others flagged in the report`,
                    o.pep_declared && o.pep_detail && `PEP: ${o.pep_detail}`,
                  ].filter(Boolean);
                  const cell = notes.length ? { ...TD, borderBottom: "none" } : TD;
                  return (
                    <React.Fragment key={o.officer_id}>
                      <tr>
                        <td style={cell}>
                          <div style={{ fontWeight: weight.semibold }}><Val value={o.full_name} required /></div>
                          <div style={{ fontSize: text.sm, color: o.role ? color.text.tertiary : TONES.danger.fg }}>{o.role || "Role missing"}</div>
                          {o.email && <div style={{ fontSize: text.sm, color: color.text.tertiary }}>{o.email}</div>}
                        </td>
                        <td style={cell}>
                          {basis.length ? (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {basis.map((b) => <span key={b} style={KEY_CHIP}>{BASIS_LABEL[b]}</span>)}
                            </div>
                          ) : <span style={{ color: color.text.tertiary }}>—</span>}
                        </td>
                        <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                          {isBlank(o.ownership_percentage) ? <Val required /> : `${o.ownership_percentage}%`}
                        </td>
                        <td style={{ ...cell, whiteSpace: "nowrap" }}><Val value={o.dob} required /></td>
                        <td style={cell}><Val value={countryLabel(o.nationality)} required /></td>
                        <td style={cell}><Val value={countryLabel(o.country_of_residence)} /></td>
                        <td style={cell}>
                          {o.pep_declared ? <span style={{ color: TONES.warning.fg, fontWeight: weight.medium }}>Yes</span> : "No"}
                        </td>
                        <td style={{ ...cell, whiteSpace: "nowrap", color: hasId ? TONES.success.fg : color.text.tertiary }}>
                          {hasId ? "Uploaded" : "Not uploaded"}
                        </td>
                      </tr>
                      {notes.length > 0 && (
                        <tr>
                          <td colSpan={8} style={{ ...TD, paddingTop: 0 }}>
                            {notes.map((n) => (
                              <div key={n} style={{ fontSize: text.xs, color: TONES.warning.fg }}>{n}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", flexWrap: "wrap", gap: `${space[1]}px ${space[4]}px`, fontSize: text.xs, color: color.text.tertiary, marginTop: space[3] }}>
              <span>Key person rules, read from the intake form:</span>
              <span><strong style={{ color: color.text.secondary, fontWeight: weight.semibold }}>CEO</strong> — role says CEO or Chief Executive</span>
              <span><strong style={{ color: color.text.secondary, fontWeight: weight.semibold }}>President</strong> — role says President (not Vice President)</span>
              <span><strong style={{ color: color.text.secondary, fontWeight: weight.semibold }}>Owner ≥25%</strong> — ownership 25% or more</span>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <label style={{ display: "flex", alignItems: "center", gap: space[2], fontSize: text.base }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          I have checked these values against the intake form and uploaded documents.
        </label>
      </Card>

      {formError && (
        <div ref={errorRef}>
          <Notice tone="danger" title="Fix this before continuing">{formError}</Notice>
        </div>
      )}

      <div style={{ display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" disabled={!confirmed || parsing} onClick={handleContinue}>{continueLabel}</Button>
        {!confirmed && (
          <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
            {data.readingConfirmedFp ? "The intake form changed — tick the confirmation again to continue." : "Tick the confirmation above to continue."}
          </span>
        )}
      </div>
    </div>
  );
}

/* ========================================================================
 * ScreeningStep (formerly ScreeningStep.jsx)
 * ========================================================================
 */
const Badge = ({ view, size, title }) =>
  view ? <StatusBadge tone={view.tone} busy={view.busy} size={size} title={title}>{view.label}</StatusBadge> : null;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const everySeconds = Math.round(POLL_EVERY_MS / 1000);

// ── document detail ───────────────────────────────────────────────────────
function DocDetail({ record, onRetry, onCheckAgain, disabled }) {
  if (!record) return null;
  const lines = [];
  for (const r of record.decline_reason || []) lines.push({ tone: "danger", text: [r.message, r.action].filter(Boolean).join(" — ") });
  if (record.error) lines.push({ tone: "danger", text: [record.error.message, record.error.action].filter(Boolean).join(" — ") });
  if (record.status === "NOT_SUBMITTED") {
    lines.push({ tone: "danger", text: record.message });
    for (const v of record.validation_results || []) {
      for (const e of v.errors || []) {
        lines.push({ tone: "danger", text: `${(v.document || "").replace(/_/g, " ")}: ${e.message}${e.suggestions?.length ? ` (${e.suggestions[0]})` : ""}` });
      }
    }
  }
  if (record.pollError) lines.push({ tone: "warning", text: `Couldn't get the latest status: ${record.pollError}` });
  if (record.timedOut) lines.push({ tone: "warning", text: "Still processing after a long wait. It may yet complete — check again shortly." });

  const ed = record.extracted_data;
  const failedToRun = record.status === "NOT_SUBMITTED" || (record.status === "ERROR" && !record.decision);
  const stale = record.pollError || record.timedOut;
  if (!ed && !lines.length && !failedToRun && !stale) return null;
  return (
    <div style={{ marginTop: space[2], fontSize: text.sm, color: color.text.secondary }}>
      {ed && (
        <div style={{ color: color.text.tertiary }}>
          Read from document: {[ed.full_name, ed.document_type && ed.document_type.replace(/_/g, " ").toLowerCase(), ed.document_country,
            ed.valid_until && `valid until ${ed.valid_until}`].filter(Boolean).join(" · ")}
        </div>
      )}
      {lines.filter((l) => l.text).map((l, i) => (
        <div key={i} style={{ color: TONES[l.tone].fg, marginTop: 2 }}>{l.text}</div>
      ))}
      {(failedToRun || stale) && (
        <div style={{ marginTop: space[2], display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
          {stale && onCheckAgain && <Button size="sm" onClick={onCheckAgain} disabled={disabled}>Check again</Button>}
          {failedToRun && onRetry && <Button size="sm" onClick={onRetry} disabled={disabled}>Retry verification</Button>}
          {failedToRun && (
            <span style={{ color: color.text.tertiary }}>To use a different file, replace it in Upload — it's verified again automatically.</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── progress ──────────────────────────────────────────────────────────────
function checkStage(key, label, marker, { running, screened, submitted }) {
  const v = checkView(marker, !running);
  const s = marker?.status;
  let state;
  if (s === "completed") state = "done";
  else if (s === "skipped") state = "skipped";
  else if (s === "failed" || s === "error") state = "error";
  else if (running && submitted) state = screened || s === "pending" ? "active" : "pending";
  else state = running ? "pending" : "skipped";
  return { key, label, state, status: state === "pending" ? null : v.label };
}

function buildStages(data) {
  const run = data.screeningRun || {};
  const running = RUNNING_PHASES.has(run.phase);
  const result = data.onboarding;
  const shown = result || run.latest;
  const submitted = !!run.applicationId;

  const kyc = shown?.kyc_summary?.officers || [];
  const errored = kyc.filter((o) => o.decision === "ERROR").length;
  const primaryDone = !!(shown?.kyb_summary?.providers || kyc.length);

  const stages = [];
  stages.push({
    key: "submit",
    label: "Submit application",
    state: run.phase === "submitting" ? "active" : run.phase === "failed" && run.errorAt === "submit" ? "error" : submitted ? "done" : "pending",
    status: run.phase === "submitting" ? "Sending" : run.errorAt === "submit" && run.phase === "failed" ? "Failed" : submitted ? "Accepted" : null,
    detail: run.deduped ? "These details were already screened — the stored result was reused" : undefined,
  });
  stages.push({
    key: "primary",
    label: "Business and owner screening",
    detail: "Sanctions, politically exposed persons and adverse media",
    state: primaryDone ? (errored ? "error" : "done") : running && submitted ? "active" : result ? "done" : "pending",
    status: primaryDone
      ? errored ? `${errored} of ${plural(kyc.length, "person", "people")} could not be screened` : `${plural(kyc.length, "person", "people")} screened`
      : running && submitted ? "Checking" : null,
  });
  const ctx = { running, screened: primaryDone, submitted };
  stages.push({ ...checkStage("registry", "Business registry lookup", shown?.kyb_summary?.kyb_registry_check, ctx), detail: "Company status and registration" });
  stages.push({ ...checkStage("aml", "Business AML check", shown?.kyb_summary?.aml_check, ctx), detail: "Matched on the legal name" });

  const docKeys = [...(hasCoi(data.merchant) ? ["merchant"] : []), ...data.officers.filter(officerHasDocs).map((o) => o.officer_id)];
  const recs = docKeys.map((k) => (k === "merchant" ? data.merchantVerification : data.officerVerifications[k]));
  const finished = recs.filter((r) => r && (isDocVerificationTerminal(r) || r.status === "NOT_SUBMITTED")).length;
  const failed = recs.filter((r) => r && (r.status === "NOT_SUBMITTED" || (r.status === "ERROR" && !r.decision))).length;
  const inFlight = recs.filter((r) => r && IN_FLIGHT.has(r.status) && !r.timedOut && !r.pollError).length;
  stages.push({
    key: "docs",
    label: "Document verification",
    detail: docKeys.length ? `${plural(docKeys.length, "document set")} — formation document and ID + selfie` : "No documents uploaded",
    state: !docKeys.length ? "skipped" : inFlight ? "active" : failed ? "error" : finished === docKeys.length ? "done" : running || submitted ? "active" : "pending",
    status: !docKeys.length ? "Skipped" : failed ? `${failed} could not be verified` : `${finished} of ${docKeys.length} complete`,
  });

  const final = result ? screeningView(result) : null;
  stages.push({
    key: "decision",
    label: "Decision",
    state: result ? (result.status === "ERROR" ? "error" : "done") : running && primaryDone ? "active" : "pending",
    status: final ? final.label : running && primaryDone ? "Waiting for checks" : null,
  });
  // Nothing past a failed submission ran — show the rest as not started.
  if (!submitted && !running) {
    return stages.map((s, i) => (i === 0 ? s : { ...s, state: "pending", status: null }));
  }
  return stages;
}

function ProgressCard({ data, runner, onBack }) {
  const run = data.screeningRun;
  const phase = run?.phase;
  const running = !run || RUNNING_PHASES.has(phase);
  const result = data.onboarding;

  let overall;
  if (!run || running) overall = { tone: "info", label: phase === "submitting" || !run ? "Starting" : "In progress", busy: true };
  else if (phase === "failed") overall = { tone: "danger", label: "Failed" };
  else if (phase === "timeout") overall = { tone: "warning", label: "Still running" };
  else overall = screeningView(result);

  return (
    <Card header={{ title: "Screening progress", right: <Badge view={overall} size="md" /> }}>
      {running && (
        <div style={{ marginBottom: space[4] }}>
          <IndeterminateBar />
          <div style={{ display: "flex", justifyContent: "space-between", gap: space[3], flexWrap: "wrap", marginTop: space[2], fontSize: text.sm, color: color.text.tertiary }}>
            <span>
              Running for <strong style={{ color: color.text.secondary }}>{run ? <Elapsed since={run.startedAt} /> : "0s"}</strong>
              {" "}· status refreshes every {everySeconds}s
            </span>
            <span>The registry and AML checks can take a few minutes. You can move between steps — it keeps running.</span>
          </div>
        </div>
      )}
      {run?.retrying && (
        <div style={{ marginBottom: space[3] }}>
          <Notice tone="warning" title="Connection problem — retrying automatically" busy>{run.retrying}</Notice>
        </div>
      )}

      <ProgressList stages={run ? buildStages(data) : [{ key: "submit", label: "Submit application", state: "active", status: "Sending" }]} />

      {phase === "failed" && run.errorAt === "submit" && (
        <div style={{ marginTop: space[4] }}>
          <Notice
            tone="danger"
            title="Screening couldn't be started"
            actions={<>
              <Button size="sm" variant="primary" onClick={runner.rerun}>Try again</Button>
              <Button size="sm" onClick={onBack}>Review details</Button>
            </>}
          >
            {run.error}
          </Notice>
        </div>
      )}
      {phase === "failed" && run.errorAt === "poll" && (
        <div style={{ marginTop: space[4] }}>
          <Notice tone="danger" title="Lost contact with the screening service"
            actions={<Button size="sm" variant="primary" onClick={runner.checkAgain}>Check again</Button>}>
            The application was submitted and is still being processed, but its status couldn't be read: {run.error}
          </Notice>
        </div>
      )}
      {phase === "timeout" && (
        <div style={{ marginTop: space[4] }}>
          <Notice tone="warning" title="Screening is taking longer than usual"
            actions={<Button size="sm" variant="primary" onClick={runner.checkAgain}>Check again</Button>}>
            The application is still being processed. Check again in a few minutes — it won't be submitted twice.
          </Notice>
        </div>
      )}
      {result?.status === "ERROR" && (
        <div style={{ marginTop: space[4] }}>
          <Notice tone="danger" title="Screening could not be completed"
            actions={<Button size="sm" variant="primary" onClick={runner.rerun}>Re-run screening</Button>}>
            {((result.edd_reasons || []).filter((c) => c !== "SCREENING_UNAVAILABLE").map((c) => EDD_TEXT[c] || c).join(" · ") ||
              "One or more checks could not be completed") + "."}
            {" "}No decision was issued, so this result isn't safe to act on. Re-running submits it again.
          </Notice>
        </div>
      )}
    </Card>
  );
}

// ── result cards ──────────────────────────────────────────────────────────
function Metric({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: text.sm, color: color.text.tertiary }}>{label}</div>
      <div style={{ fontSize: text.lg, fontWeight: weight.semibold, color: color.text.primary, marginTop: 2 }}>{children}</div>
    </div>
  );
}

function DecisionCard({ result }) {
  const view = screeningView(result);
  const risk = RISK_VIEW[result.risk_level];
  const reasons = result.edd_reasons || [];
  return (
    <Card header={{
      title: "Screening decision",
      right: <div style={{ display: "flex", gap: space[2] }}><Badge view={view} size="md" />{risk && <Badge view={risk} size="md" />}</div>,
    }}>
      <div style={{ display: "flex", gap: space[6], flexWrap: "wrap" }}>
        <Metric label="Outcome"><span style={{ color: TONES[view.tone].fg }}>{view.label}</span></Metric>
        <Metric label="Enhanced due diligence">
          {result.edd_required ? <span style={{ color: TONES.warning.fg }}>Required</span> : result.edd_required === false ? "Not required" : "—"}
        </Metric>
      </div>
      {reasons.length > 0 && (
        <div style={{ marginTop: space[4], display: "flex", gap: space[2], flexWrap: "wrap" }}>
          {reasons.map((r) => <StatusBadge key={r} tone={eddTone(r)}>{EDD_TEXT[r] || r.replace(/_/g, " ").toLowerCase()}</StatusBadge>)}
        </div>
      )}
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", gap: space[4], padding: `${space[2]}px 0`, borderBottom: `1px solid ${color.border.subtle}`, fontSize: text.base, alignItems: "center" }}>
      <span style={{ color: color.text.muted, width: "34%", flexShrink: 0 }}>{label}</span>
      <span style={{ color: color.text.primary, flex: 1, minWidth: 0, wordBreak: "break-word" }}>{children}</span>
    </div>
  );
}

// Why a registry / AML check was skipped or failed (the marker's `message`).
const markerNote = (m) => (m && ["skipped", "failed", "error"].includes(m.status) ? m.message : null);

function KybCard({ shown, running }) {
  const kyb = shown.kyb_summary || {};
  const primary = kyb.providers?.screening || {};
  const reg = kyb.kyb_registry_check;
  const aml = kyb.aml_check;
  const primaryRisk = RISK_VIEW[primary.risk_level];
  const tags = (primary.risk_types || []).map((t) => t.replace(/^r_/, "").replace(/_/g, " "));
  return (
    <Card header={{ title: "Business screening" }}>
      <Row label="Matched entity">{primary.legal_name || "—"}</Row>
      <Row label="Screening risk">
        {primaryRisk ? <Badge view={primaryRisk} /> : "—"}
        {primary.score != null && <span style={{ marginLeft: space[2], color: color.text.tertiary }}>match {Number(primary.score).toFixed(2)}</span>}
      </Row>
      <Row label="Risk indicators">
        {tags.length ? (
          <span style={{ display: "inline-flex", gap: space[1], flexWrap: "wrap" }}>
            {tags.map((t) => <StatusBadge key={t} tone="warning">{t}</StatusBadge>)}
          </span>
        ) : "None"}
      </Row>
      <Row label="Registry lookup">
        <span style={{ display: "inline-flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
          <Badge view={checkView(reg, !running)} />
          <span style={{ color: color.text.secondary }}>
            {[reg?.best_match?.name, reg?.best_match?.status, RISK_VIEW[reg?.risk_level]?.label, markerNote(reg)].filter(Boolean).join(" · ")}
          </span>
        </span>
      </Row>
      <Row label="AML check">
        <span style={{ display: "inline-flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
          <Badge view={checkView(aml, !running)} />
          <span style={{ color: color.text.secondary }}>
            {[aml?.match_status && aml.match_status.replace(/_/g, " "), aml?.hit_count != null ? plural(aml.hit_count, "hit") : null,
              (aml?.categories || []).join(", "), markerNote(aml)].filter(Boolean).join(" · ")}
          </span>
        </span>
      </Row>
    </Card>
  );
}

function ScreeningStep({ data, runner, onNext, onBack }) {
  // Starts a run only if there's none yet or the confirmed data changed —
  // coming back to this step never resubmits an unchanged screening.
  useEffect(() => {
    runner.ensure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = data.screeningRun;
  const running = !run || RUNNING_PHASES.has(run.phase);
  const result = data.onboarding;
  const shown = result || run?.latest;
  const kycById = Object.fromEntries((shown?.kyc_summary?.officers || []).map((o) => [o.officer_id, o]));
  const mv = data.merchantVerification;
  const docRecs = [mv, ...Object.values(data.officerVerifications || {})].filter(Boolean);
  const docsPending = docRecs.some((r) => IN_FLIGHT.has(r.status) && !r.timedOut && !r.pollError);
  const noKey = !run?.applicationId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
      <ProgressCard data={data} runner={runner} onBack={onBack} />

      {result && <DecisionCard result={result} />}

      {shown?.kyb_summary && <KybCard shown={shown} running={running} />}

      <Card header={{
        title: "Formation document",
        right: hasCoi(data.merchant) ? <Badge view={docView(mv)} /> : <StatusBadge tone="neutral">Not uploaded</StatusBadge>,
      }}>
        <div style={{ fontSize: text.base, color: hasCoi(data.merchant) ? color.text.primary : color.text.tertiary }}>
          {data.merchant.documents.certificate_of_incorporation?.file_name || "No formation document was uploaded — optional."}
        </div>
        <DocDetail record={mv} disabled={noKey} onRetry={() => runner.retryDoc("merchant")} onCheckAgain={() => runner.checkDocAgain("merchant")} />
      </Card>

      <Card header={{ title: "Owners and control persons" }}>
        {data.officers.length === 0 && <div style={{ fontSize: text.base, color: color.text.tertiary }}>No people were submitted.</div>}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {data.officers.map((o, i) => {
            const k = kycById[o.officer_id];
            const kycViewRec = officerKycView(k, running);
            const risk = RISK_VIEW[k?.risk_level];
            const dv = data.officerVerifications[o.officer_id];
            const reasons = (k?.reasons || []).map((r) => r.message).filter(Boolean);
            return (
              <div key={o.officer_id} data-officer={o.full_name}
                style={{ padding: `${space[3]}px 0`, borderTop: i ? `1px solid ${color.border.subtle}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: space[3], flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: weight.semibold, color: color.text.primary }}>{o.full_name}</div>
                    <div style={{ fontSize: text.sm, color: color.text.tertiary }}>
                      {[o.role, o.ownership_percentage !== "" && o.ownership_percentage != null ? `${o.ownership_percentage}% ownership` : null, o.pep_declared ? "PEP declared" : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: text.xs, color: color.text.tertiary }}>Screening</span>
                    <Badge view={kycViewRec} />
                    {risk && <Badge view={risk} />}
                    <span style={{ fontSize: text.xs, color: color.text.tertiary, marginLeft: space[2] }}>ID check</span>
                    {officerHasDocs(o) ? <Badge view={docView(dv)} /> : <StatusBadge tone="neutral">No ID + selfie</StatusBadge>}
                  </div>
                </div>
                {k?.decision === "ERROR" && (
                  <div style={{ marginTop: space[1], fontSize: text.sm, color: TONES.danger.fg }}>
                    Screening for this person couldn't be completed, so there is no decision for them yet. Re-run
                    screening once the current run has finished.
                  </div>
                )}
                {k && k.decision !== "APPROVE" && k.decision !== "ERROR" && reasons.length > 0 && (
                  <div style={{ marginTop: space[1], fontSize: text.sm, color: TONES[kycViewRec.tone].fg }}>{reasons.join(" · ")}</div>
                )}
                <DocDetail record={dv} disabled={noKey} onRetry={() => runner.retryDoc(o.officer_id)} onCheckAgain={() => runner.checkDocAgain(o.officer_id)} />
              </div>
            );
          })}
        </div>
      </Card>

      {result && docsPending && (
        <Notice tone="info" title="Document checks are still running" busy>
          You can continue to the report now — unfinished checks show as in progress, and the report updates when you
          regenerate it.
        </Notice>
      )}

      <div style={{ display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={onNext} disabled={!result}>Continue to report</Button>
        {!result && (
          <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
            {running ? "Available once screening finishes." : "Available once screening has a result."}
          </span>
        )}
      </div>
    </div>
  );
}

/* ========================================================================
 * NotesPanel (formerly NotesPanel.jsx) — notes on the screened application,
 * each tagged to the business, key persons (CEO, President, owners of 25%+)
 * and/or "Other Officers" (everyone else). They are part of the compliance
 * record, so nothing changes without an explicit action: notes are read-only
 * until "Edit", every change needs "Save" (Cancel discards it), and a delete
 * asks for confirmation.
 * ========================================================================
 */
const MAX_CHARS = 4000;

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const pill = (on) => ({
  fontFamily: "inherit", fontSize: text.sm, fontWeight: on ? weight.semibold : weight.regular, cursor: "pointer",
  minHeight: 32, padding: `${space[1]}px ${space[3]}px`, borderRadius: radius.pill,
  color: on ? color.brand.solid : color.text.secondary,
  background: on ? color.brand.subtle : color.bg.surface,
  border: `1px solid ${on ? color.brand.solid : color.border.strong}`,
});

const chip = (business) => ({
  fontSize: text.xs, fontWeight: weight.semibold, padding: `1px ${space[2]}px`, borderRadius: radius.sm,
  color: business ? color.text.secondary : color.brand.solid,
  background: business ? color.bg.subtle : color.brand.subtle,
  border: `1px solid ${business ? color.border.subtle : color.brand.border}`,
});

function NotesPanel({ applicationId, notes, setNotes, entities, footer }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { id: number | "new", text, tags }
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // note id
  const [filter, setFilter] = useState(null); // entity id or null = all

  // The application the panel is showing now — a save or delete that finishes
  // after "Start a new onboarding" (or a new screening) is dropped.
  const currentApp = useRef(applicationId);
  currentApp.current = applicationId;
  const taggable = tagOptions(entities);
  const byId = Object.fromEntries([...(entities || []), ...taggable].map((e) => [e.id, e]));

  useEffect(() => {
    if (!applicationId) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    listNotes(applicationId)
      .then((list) => live && setNotes(list))
      .catch((e) => live && setError(`Couldn't load the notes. ${e.message}`))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [applicationId, setNotes]);

  async function save() {
    const value = editing.text.trim();
    if (!value || !editing.tags.length) return;
    setSaving(true);
    setError(null);
    try {
      const forApp = applicationId;
      const saved = await saveNote(forApp, editing.id === "new" ? null : editing.id, value, editing.tags);
      if (currentApp.current !== forApp) return;
      setNotes((list) => (editing.id === "new" ? [...list, saved] : list.map((n) => (n.id === saved.id ? saved : n))));
      setEditing(null);
    } catch (e) {
      setError(`Couldn't save the note. ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    setSaving(true);
    setError(null);
    try {
      const forApp = applicationId;
      await deleteNote(forApp, id);
      if (currentApp.current !== forApp) return;
      setNotes((list) => list.filter((n) => n.id !== id));
      setConfirmDelete(null);
    } catch (e) {
      setError(`Couldn't delete the note. ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  const toggleTag = (id) =>
    setEditing((ed) => ({ ...ed, tags: ed.tags.includes(id) ? ed.tags.filter((t) => t !== id) : [...ed.tags, id] }));

  const noTags = !!editing && editing.tags.length === 0;
  const editor = (
    <div style={{ display: "flex", flexDirection: "column", gap: space[3] }}>
      <TextArea
        id={`note-${editing?.id}`}
        aria-label="Note"
        autoFocus
        maxLength={MAX_CHARS}
        value={editing?.text || ""}
        onChange={(e) => setEditing((ed) => ({ ...ed, text: e.target.value }))}
        placeholder="Write the note."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: space[1] }}>
        <div style={{ fontSize: text.xs, fontWeight: weight.semibold, color: color.text.secondary }}>
          Tag to <span style={{ fontWeight: weight.regular, color: color.text.tertiary }}>— pick at least one</span>
        </div>
        <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
          {taggable.map((e) => {
            const on = !!editing?.tags.includes(e.id);
            return (
              <button key={e.id} type="button" aria-pressed={on} onClick={() => toggleTag(e.id)} style={pill(on)}>
                {on ? "✓ " : ""}{entityLabel(e)}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
        <Button size="sm" variant="primary" onClick={save} disabled={saving || !editing?.text.trim() || noTags}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
        {noTags && editing?.text.trim() && (
          <span style={{ fontSize: text.sm, color: color.danger.text }}>Pick who this note is about.</span>
        )}
        <span style={{ marginLeft: "auto", fontSize: text.xs, color: color.text.tertiary }}>
          {(editing?.text || "").length} / {MAX_CHARS}
        </span>
      </div>
    </div>
  );

  const shown = filter ? notes.filter((n) => (n.tags || []).includes(filter)) : notes;
  const count = (id) => notes.filter((n) => (n.tags || []).includes(id)).length;

  return (
    <Card
      header={{
        title: "Notes",
        right: loading ? <Spinner size={14} label="Loading notes" /> : (
          <StatusBadge tone="neutral">{notes.length} {notes.length === 1 ? "note" : "notes"}</StatusBadge>
        ),
      }}
    >
      <div style={{ fontSize: text.sm, color: color.text.tertiary, marginBottom: space[3] }}>
        Saved with this application as part of the compliance record. Tag each note to the business, a key person or Other Officers.
      </div>

      {notes.length > 0 && (
        <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap", marginBottom: space[3] }}>
          <span style={{ fontSize: text.xs, color: color.text.tertiary }}>Show</span>
          <button type="button" aria-pressed={!filter} onClick={() => setFilter(null)} style={pill(!filter)}>All · {notes.length}</button>
          {taggable.map((e) => (
            <button key={e.id} type="button" aria-pressed={filter === e.id} onClick={() => setFilter(e.id)} style={pill(filter === e.id)}>
              {entityLabel(e)} · {count(e.id)}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: space[3] }}>
        {shown.map((n) => (
          <div key={n.id} style={{ border: `1px solid ${color.border.subtle}`, borderRadius: radius.md, padding: space[3] }}>
            {editing?.id === n.id ? editor : (
              <>
                <div style={{ fontSize: text.base, color: color.text.primary, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{n.text}</div>
                <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap", marginTop: space[2] }}>
                  {(n.tags || []).map((t) => (
                    <span key={t} style={chip(t === BUSINESS_ENTITY)}>{byId[t] ? entityLabel(byId[t]) : t}</span>
                  ))}
                  <span style={{ fontSize: text.xs, color: color.text.tertiary }}>
                    {n.updated_at && n.updated_at !== n.created_at ? `Edited ${when(n.updated_at)}` : `Added ${when(n.created_at)}`}
                  </span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: space[2] }}>
                    {confirmDelete === n.id ? (
                      <>
                        <span style={{ fontSize: text.sm, color: color.text.secondary, alignSelf: "center" }}>Delete this note?</span>
                        <Button size="sm" variant="secondary" onClick={() => remove(n.id)} disabled={saving}>Delete</Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} disabled={saving}>Keep</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ id: n.id, text: n.text, tags: [...(n.tags || [])] })} disabled={!!editing || saving}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(n.id)} disabled={!!editing || saving}>Delete</Button>
                      </>
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
        ))}

        {!loading && notes.length === 0 && editing?.id !== "new" && (
          <div style={{ fontSize: text.base, color: color.text.tertiary }}>No notes yet.</div>
        )}
        {notes.length > 0 && shown.length === 0 && (
          <div style={{ fontSize: text.base, color: color.text.tertiary }}>No notes tagged to {entityLabel(byId[filter])}.</div>
        )}

        {editing?.id === "new" ? (
          <div style={{ border: `1px solid ${color.border.subtle}`, borderRadius: radius.md, padding: space[3] }}>{editor}</div>
        ) : (
          <div>
            <Button size="sm" variant="secondary" onClick={() => setEditing({ id: "new", text: "", tags: filter ? [filter] : [] })} disabled={!!editing || loading}>
              Add note
            </Button>
          </div>
        )}
      </div>

      {error && <div style={{ marginTop: space[3] }}><Notice tone="danger" title="Notes">{error}</Notice></div>}
      {footer && <div style={{ marginTop: space[4], paddingTop: space[3], borderTop: `1px solid ${color.border.subtle}` }}>{footer}</div>}
    </Card>
  );
}

/* ========================================================================
 * ReportStep (formerly ReportStep.jsx)
 * ========================================================================
 */
// One severity scale across the flow and both reports.
const SEVERITY = {
  high: { tone: "danger", label: "High", meaning: "must be resolved before onboarding" },
  medium: { tone: "warning", label: "Medium", meaning: "needs review" },
  low: { tone: "info", label: "Low", meaning: "for information" },
};

// Two user-facing PDFs are generated together from the same records:
//   summary  — screening outcome, findings, business + owner screening (2 pages)
//   detailed — adds profile, documents, activity, disclosures (≈6 pages)
// Neither contains internal ids, keys, codes or scores; sensitive numbers are
// masked server-side (services/tfc_onboarding/onboarding_report/present.py).
const VARIANTS = {
  summary: { label: "Summary", button: "Download report", suffix: "Due-Diligence-Report" },
  detailed: { label: "Detailed", button: "Download detailed report", suffix: "Due-Diligence-Report-Detailed" },
};

// The backend fetches the screening and the document verification records
// itself (by id); the page only sends the parsed intake form, what the
// analyst confirmed, and which verifications belong to this onboarding.
function documentIds(data) {
  return Object.fromEntries(
    Object.entries(data.docRuns || {}).filter(([, r]) => Number.isInteger(r.applicationId)).map(([k, r]) => [k, r.applicationId])
  );
}

function buildReportRequest(data) {
  const screened = buildScreeningPayload(data);
  return {
    documents: documentIds(data),
    intake_form: data.intakeFormParsed || undefined,
    // What the analyst confirmed and submitted for screening.
    confirmed: {
      merchant: { ...screened.merchant, reference_id: screened.reference_id, incorporation: resolveIncorporation(data.merchant) },
      officers: data.officers.map((o, i) => ({
        ...screened.officers[i],
        pep_declared: !!o.pep_declared,
        pep_detail: o.pep_detail || undefined,
        id_doc_type: o.id_doc_type,
      })),
    },
    uploads: {
      merchant_documents: Object.fromEntries(
        Object.entries(data.merchant.documents).filter(([, d]) => d && d.file_name).map(([k, d]) => [k, d.file_name])
      ),
      supporting_docs: (data.supportingDocs || []).map((d) => d.file_name),
      // Type of the document in the certificate_of_incorporation slot.
      formation_document_type: data.formationDocType || undefined,
    },
    checklist: data.docChecklist,
    // Notes are printed only when the analyst chose to include them.
    ...(data.includeNotes && (data.notes || []).length
      ? { include_notes: true, notes: data.notes.map((n) => ({ text: n.text, tags: n.tags || [], created_at: n.created_at, updated_at: n.updated_at })) }
      : {}),
  };
}

// What the report was built from — a change means the PDFs are out of date.
const reportInputs = (data) => fingerprint({ ...buildReportRequest(data), v: data.resultsVersion || 0 });

function fileName(report, legalName, variant) {
  const name = (legalName || "Merchant").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "Merchant";
  const day = report?.generated_at ? report.generated_at.slice(0, 10) : "";
  return [name, VARIANTS[variant].suffix, day].filter(Boolean).join("-") + ".pdf";
}

const countsText = (items) =>
  ["high", "medium", "low"].map((sv) => [sv, items.filter((f) => f.severity === sv).length]).filter(([, n]) => n);

function ItemRows({ findings, actions }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[2], fontSize: text.base }}>
      {findings.map((f, i) => (
        <div key={`f${i}`} style={{ display: "grid", gridTemplateColumns: "84px 1fr", gap: space[2], alignItems: "start" }}>
          <span><StatusBadge tone={SEVERITY[f.severity]?.tone || "neutral"}>{SEVERITY[f.severity]?.label || f.severity}</StatusBadge></span>
          <span>{f.text}</span>
        </div>
      ))}
      {findings.length === 0 && <div style={{ color: color.text.tertiary }}>No findings.</div>}
      {actions.length > 0 && (
        <>
          <div style={{ fontSize: text.xs, fontWeight: weight.semibold, color: color.text.secondary, paddingTop: space[1] }}>Recommended actions</div>
          {actions.map((a, i) => <div key={`a${i}`} style={{ color: color.text.secondary }}>→ {a.text}</div>)}
        </>
      )}
    </div>
  );
}

// Findings and recommended actions grouped by the business, each key person,
// then Other Officers — the same groups as both PDFs.
function ReportItems({ report, notes }) {
  const findings = report.findings || [];
  const actions = report.actions || [];
  const of = (id) => ({ f: findings.filter((x) => x.entity === id), a: actions.filter((x) => x.entity === id) });
  const keyOnes = report.entities.filter((e) => e.key);
  const others = report.entities.filter((e) => !e.key).filter((e) => of(e.id).f.length || of(e.id).a.length);
  const noteCount = (id) => notes.filter((n) => (n.tags || []).includes(id)).length;
  const chipStyle = {
    fontSize: text.xs, fontWeight: weight.semibold, color: color.brand.solid, background: color.brand.subtle,
    border: `1px solid ${color.brand.border}`, borderRadius: radius.sm, padding: "1px 8px",
  };
  const head = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[3], flexWrap: "wrap",
    padding: `${space[2]}px ${space[3]}px`, background: color.bg.subtle, borderBottom: `1px solid ${color.border.subtle}` };
  const Counts = ({ items, id }) => (
    <span style={{ fontSize: text.xs, color: color.text.tertiary }}>
      {countsText(items).map(([sv, n], k) => (
        <span key={sv}>{k ? " · " : ""}<strong style={{ color: sv === "high" ? TONES.danger.fg : sv === "medium" ? TONES.warning.fg : color.text.primary }}>{n}</strong> {sv}</span>
      ))}
      {id ? `${countsText(items).length ? " · " : ""}${noteCount(id) || "no"} ${noteCount(id) === 1 ? "note" : "notes"}` : ""}
    </span>
  );
  return (
    <Card header={{ title: "Report items", right: <span style={{ fontSize: text.xs, color: color.text.tertiary }}>Grouped by the business and its key persons</span> }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: `${space[1]}px ${space[4]}px`, fontSize: text.xs, color: color.text.tertiary, marginBottom: space[3] }}>
        {Object.values(SEVERITY).map((sv) => (
          <span key={sv.label} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: TONES[sv.tone].solid }} />
            {sv.label} — {sv.meaning}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: space[3] }}>
        {keyOnes.map((e) => {
          const { f, a } = of(e.id);
          return (
            <div key={e.id} style={{ border: `1px solid ${color.border.subtle}`, borderRadius: radius.lg, overflow: "hidden" }}>
              <div style={head}>
                <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: text.md, fontWeight: weight.semibold }}>{e.type === "BUSINESS" ? "Business" : e.name}</span>
                  {e.type === "BUSINESS" && <span style={{ fontSize: text.sm, color: color.text.tertiary }}>{e.name}</span>}
                  {(e.basis || []).filter((b) => b !== "OWNER_25").map((b) => <span key={b} style={chipStyle}>{BASIS_LABEL[b]}</span>)}
                  {(e.basis || []).includes("OWNER_25") && e.ownership_percentage != null && <span style={chipStyle}>{e.ownership_percentage}%</span>}
                </div>
                <Counts items={f} id={e.id} />
              </div>
              <div style={{ padding: space[3] }}><ItemRows findings={f} actions={a} /></div>
            </div>
          );
        })}
        {others.length > 0 && (
          <div style={{ border: `1px dashed ${color.border.strong}`, borderRadius: radius.lg, overflow: "hidden" }}>
            <div style={{ ...head, background: color.bg.surface, borderBottom: `1px dashed ${color.border.strong}` }}>
              <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.secondary }}>Other Officers</span>
                <span style={{ fontSize: text.sm, color: color.text.tertiary }}>Under 25% and not CEO or President — tagged together as "Other Officers"</span>
              </div>
              <Counts items={others.flatMap((e) => of(e.id).f)} id="OTHER_OFFICERS" />
            </div>
            <div style={{ padding: space[3], display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: space[4] }}>
              {others.map((e) => (
                <div key={e.id} style={{ display: "flex", flexDirection: "column", gap: space[2], minWidth: 0 }}>
                  <div style={{ fontWeight: weight.semibold, color: color.text.secondary }}>
                    {e.name} <span style={{ fontWeight: weight.regular, color: color.text.tertiary }}>
                      {[e.role, e.ownership_percentage != null ? `${e.ownership_percentage}%` : null].filter(Boolean).map((x) => ` · ${x}`).join("")}
                    </span>
                  </div>
                  <ItemRows findings={of(e.id).f} actions={of(e.id).a} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function ReportStep({ data, setData, onBack, onScreening, onStartOver }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // { title, message, retry }
  const [preview, setPreview] = useState("summary");
  const [blobs, setBlobs] = useState({}); // "report_id:variant" -> object URL
  const [loading, setLoading] = useState({}); // variant -> bool
  const autoRunStarted = useRef(false);
  const blobsRef = useRef({});
  const mounted = useRef(true);
  const busyRef = useRef(false);
  const report = data.report;
  const setNotes = useCallback(
    (next) => setData((s) => ({ ...s, notes: typeof next === "function" ? next(s.notes || []) : next })),
    [setData]
  );
  const dataRef = useRef(data);
  dataRef.current = data;

  // Object URLs hold the PDF in memory — release them on unmount.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      Object.values(blobsRef.current).forEach((u) => URL.revokeObjectURL(u));
      blobsRef.current = {};
    };
  }, []);

  // The report may only combine the page's details with the screening that
  // was run for exactly those details.
  const screeningCurrent = !!data.onboarding && data.screeningRun?.phase === "done" &&
    data.screeningRun.fingerprint === screeningFingerprint(data) &&
    data.screeningRun.applicationId === data.onboarding.application_id;
  const inputs = screeningCurrent ? reportInputs(data) : null;
  const stale = !!report && report.inputs !== inputs;

  function resetBlobs() {
    Object.values(blobsRef.current).forEach((u) => URL.revokeObjectURL(u));
    blobsRef.current = {};
    setBlobs({});
  }

  // Fetched for preview + download; cached per report and variant, so a
  // regenerated report never shows the previous file.
  async function ensurePdf(meta, variant) {
    const key = `${meta.report_id}:${variant}`;
    if (blobsRef.current[key]) return blobsRef.current[key];
    setLoading((l) => ({ ...l, [variant]: true }));
    try {
      const blob = await fetchReportPdf(dataRef.current.onboarding.application_id, variant);
      const url = URL.createObjectURL(blob);
      if (!mounted.current || blobsRef.current[key]) {
        URL.revokeObjectURL(url);
        return blobsRef.current[key] || null;
      }
      blobsRef.current = { ...blobsRef.current, [key]: url };
      setBlobs(blobsRef.current);
      return url;
    } finally {
      if (mounted.current) setLoading((l) => ({ ...l, [variant]: false }));
    }
  }

  async function generate() {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    resetBlobs();
    const d = dataRef.current;
    const snapshot = reportInputs(d);
    try {
      const meta = await generateReport(d.onboarding.application_id, buildReportRequest(d));
      if (!mounted.current) return;
      setData((s) => ({ ...s, report: { ...meta, inputs: snapshot } }));
      await ensurePdf(meta, preview);
    } catch (e) {
      if (mounted.current) setError({ title: "The report couldn't be generated", message: e.message, retry: generate });
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function download(variant) {
    setError(null);
    try {
      const url = await ensurePdf(report, variant);
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName(report, data.merchant.legal_business_name, variant);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError({ title: "The download failed", message: e.message, retry: () => download(variant) });
    }
  }

  function showPreview(variant) {
    setPreview(variant);
    if (report) {
      ensurePdf(report, variant).catch((e) =>
        setError({ title: "The preview couldn't be loaded", message: e.message, retry: () => showPreview(variant) }));
    }
  }

  // First visit generates; a later visit reuses the stored report unless the
  // results or inputs changed since.
  useEffect(() => {
    if (!screeningCurrent || autoRunStarted.current) return;
    autoRunStarted.current = true;
    if (!report || stale) generate();
    else {
      ensurePdf(report, "summary").catch((e) =>
        setError({ title: "The preview couldn't be loaded", message: e.message, retry: () => showPreview("summary") }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!screeningCurrent) {
    const changed = !!data.screeningRun && data.screeningRun.fingerprint !== screeningFingerprint(data);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
        <Notice
          tone="warning"
          title={changed ? "Intake form changed since the last screening" : "No screening result yet"}
          actions={<Button size="sm" variant="primary" onClick={onScreening || onBack}>{changed ? "Run screening again" : "Go to screening"}</Button>}
        >
          {changed
            ? "The report must match what was screened. Run screening on the current intake form, then generate the report."
            : "Finish screening before generating the report."}
        </Notice>
        <div><Button variant="secondary" onClick={onBack}>Back</Button></div>
      </div>
    );
  }

  const ready = !!report && !busy;
  const docsPending = [data.merchantVerification, ...Object.values(data.officerVerifications || {})]
    .some((r) => r && IN_FLIGHT.has(r.status) && !r.timedOut && !r.pollError);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
      {ready && (report.entities || []).length > 0 && <ReportItems report={report} notes={data.notes || []} />}

      <NotesPanel
        applicationId={data.onboarding.application_id}
        entities={keyEntities(data.merchant.legal_business_name, data.officers)}
        notes={data.notes || []}
        setNotes={setNotes}
        footer={(
          <label style={{ display: "flex", alignItems: "flex-start", gap: space[2], fontSize: text.base }}>
            <input
              type="checkbox"
              checked={!!data.includeNotes}
              onChange={(e) => setData((s) => ({ ...s, includeNotes: e.target.checked }))}
              style={{ marginTop: 3 }}
            />
            <span>
              Include notes in the downloadable reports
              <span style={{ display: "block", fontSize: text.xs, color: color.text.tertiary }}>
                Off by default. Regenerate the report after changing this or the notes.
              </span>
            </span>
          </label>
        )}
      />

      {ready && stale && (
        <Notice tone="info" title="The report is out of date"
          actions={<Button size="sm" variant="primary" onClick={generate}>Regenerate</Button>}>
          Results or notes have changed since this report was generated. Regenerate to include them.
        </Notice>
      )}
      {ready && !stale && docsPending && (
        <Notice tone="info" title="Document checks are still running" busy>
          They show as in progress in this report. You'll be prompted to regenerate once they finish.
        </Notice>
      )}
      {data.onboarding.status === "ERROR" && (
        <Notice tone="warning" title="Screening didn't complete">
          This report records an incomplete screening with no decision. Re-run screening before relying on it.
        </Notice>
      )}

      {error && (
        <Notice tone="danger" title={error.title}
          actions={error.retry && <Button size="sm" onClick={error.retry} disabled={busy}>Try again</Button>}>
          {error.message}
        </Notice>
      )}

      <Card header={{ title: "Report", right: busy ? <StatusBadge tone="info" busy size="md">Generating</StatusBadge> : null }}>
        <div style={{ display: "flex", gap: space[3], flexWrap: "wrap", alignItems: "center" }}>
          <Button variant={report ? "secondary" : "primary"} onClick={generate} disabled={busy}>{report ? "Regenerate" : "Generate"}</Button>
          {ready && (
            <>
              <Button variant="primary" onClick={() => download("summary")} disabled={loading.summary}>
                {loading.summary && !blobs[`${report.report_id}:summary`] ? "Preparing…" : VARIANTS.summary.button}
              </Button>
              <Button variant="secondary" onClick={() => download("detailed")} disabled={loading.detailed}>
                {loading.detailed && !blobs[`${report.report_id}:detailed`] ? "Preparing…" : VARIANTS.detailed.button}
              </Button>
            </>
          )}
          {ready && (
            <div style={{ marginLeft: "auto" }}>
                <div role="tablist" style={{ display: "flex", gap: space[1] }}>
                  {Object.entries(VARIANTS).map(([key, v]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={preview === key}
                      onClick={() => showPreview(key)}
                      style={{
                        padding: `${space[1]}px ${space[3]}px`, borderRadius: 6, cursor: "pointer", fontSize: text.sm,
                        fontWeight: weight.medium, fontFamily: "inherit",
                        border: `1px solid ${preview === key ? color.brand.solid : color.border.strong}`,
                        background: preview === key ? color.brand.subtle : color.bg.surface,
                        color: preview === key ? color.brand.solid : color.text.secondary,
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
            </div>
          )}
        </div>
        {busy && <div style={{ marginTop: space[4] }}><IndeterminateBar /></div>}
        {ready && (
          <div style={{ marginTop: space[4] }}>
            {blobs[`${report.report_id}:${preview}`] ? (
              <iframe
                title={`${VARIANTS[preview].label} report preview`}
                src={blobs[`${report.report_id}:${preview}`]}
                style={{ width: "100%", height: "80vh", border: `1px solid ${color.border.subtle}`, borderRadius: 6 }}
              />
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: space[2], fontSize: text.base, color: color.text.tertiary, padding: space[4] }}>
                {loading[preview] ? <><Spinner size={14} /> Loading preview…</> : "Preview unavailable."}
              </div>
            )}
          </div>
        )}
      </Card>

      <div style={{ display: "flex", gap: space[3], flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={onBack}>Back</Button>
        {onStartOver && (
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm("Start a new onboarding? The current details and results will be cleared from this page.")) onStartOver();
            }}
          >
            Start a new onboarding
          </Button>
        )}
      </div>
    </div>
  );
}

/* ========================================================================
 * The wizard itself (formerly OnboardingPage.jsx)
 * ========================================================================
 */
const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };

function emptyMerchant() {
  return {
    merchant_id: `mer-${uid().replace(/-/g, "").slice(0, 12)}`,
    reference_id: "",
    legal_business_name: "",
    business_type: "",
    contact_email: "",
    tax_id: "",
    registration_number: "",
    incorporation: { country: "", state: "", registered_address: { ...EMPTY_ADDRESS } },
    operating_address: { ...EMPTY_ADDRESS },
    documents: {
      certificate_of_incorporation: null,
      irs_ein_letter: null,
      business_license: null,
      state_licenses_bundle: null,
    },
  };
}

function initialData() {
  return {
    merchant: emptyMerchant(),
    officers: [],
    intakeFormFile: null,
    formationDocType: "", // FORMATION_DOC_TYPES value for the business document (UI only)
    supportingDocs: [], // no longer collected; kept empty for the report request
    // Derived from the parsed form + uploads (checklist.js); frozen into
    // docChecklist on Continue.
    docChecklist: Object.fromEntries(CHECKLIST_ITEMS.map((item) => [item, false])),
    checklistOverrides: {},
    checklistConfirmed: false,
    intakeFormParsed: null,
    readingConfirmedFp: null, // fingerprint of the values the analyst confirmed
    // Screening run (useScreeningRunner): fingerprint of what was submitted,
    // application id, phase and the latest polled record.
    screeningRun: null,
    onboarding: null, // final GET /v1/onboarding/{id} record
    verificationKey: null, // links document verifications to the screening
    merchantVerification: null,
    officerVerifications: {},
    docRuns: {}, // "merchant" | officer_id → { fingerprint, applicationId }
    resultsVersion: 0, // bumps whenever a screening / verification result lands
    report: null, // POST /v1/onboarding-reports metadata
    notes: [], // analyst notes on the screened application (stored server-side)
    includeNotes: false, // print the notes in the downloadable reports
  };
}

function OnboardingWizard() {
  const [step, setStep] = useState("upload");
  const [furthestIndex, setFurthestIndex] = useState(0);
  const [data, setData] = useState(initialData);
  const runner = useScreeningRunner(data, setData);

  const phase = data.screeningRun?.phase;
  const screeningBusy = RUNNING_PHASES.has(phase);

  // Nothing is persisted in the page, so leaving it loses the work (a running
  // screening continues server-side) — ask first whenever there is any.
  const hasWork = !!(data.intakeFormFile || data.screeningRun);
  useEffect(() => {
    if (!hasWork) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasWork]);

  function goTo(key) {
    setStep(key);
    setFurthestIndex((i) => Math.max(i, STEP_KEYS.indexOf(key)));
    if (typeof window !== "undefined" && window.scrollTo) window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Nothing is persisted: a page refresh always starts empty. These reset
  // in-page state and abandon any screening still being polled.
  function startOver() {
    runner.reset();
    setData(initialData());
    setFurthestIndex(0);
    setStep("upload");
  }

  function newOnboarding(intakeFormFile) {
    runner.reset();
    setData({ ...initialData(), intakeFormFile });
    setFurthestIndex(0);
  }

  const stepStatus = {
    screening: screeningBusy ? "busy" : phase === "failed" || phase === "timeout" || data.onboarding?.status === "ERROR" ? "error" : undefined,
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: `${space[6]}px ${space[5]}px` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: space[4], marginBottom: space[1] }}>
        <h1 style={{ margin: 0, fontSize: text["2xl"], fontWeight: weight.semibold, color: color.text.primary }}>
          Merchant onboarding intake
        </h1>
        <span style={{ fontSize: text.sm, color: color.text.tertiary, flexShrink: 0 }}>
          <strong style={{ color: color.text.primary, fontWeight: weight.semibold }}>Terraform Capital</strong> - Powered by Idenza
        </span>
      </div>
      <div
        style={{
          position: "fixed",
          top: space[4],
          right: space[5],
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: space[2],
        }}
      >
        <button
          type="button"
          onClick={tfcLogout}
          title="Log out"
          style={{
            background: color.bg.surface,
            border: `2px solid ${color.border.strong}`,
            borderRadius: radius.md,
            padding: `${space[2]}px ${space[3]}px`,
            fontSize: text.sm,
            color: color.text.secondary,
            cursor: "pointer",
            boxShadow: shadow.lg,
          }}
        >
          Log Out
        </button>
      </div>
      <div style={{ marginBottom: space[6], fontSize: text.md, color: color.text.tertiary }}>
        Upload the CDD intake form and documents, confirm what was read, screen the business and its owners,
        then download the due-diligence report.
      </div>

      <Stepper activeKey={step} furthestIndex={furthestIndex} onSelect={goTo} status={stepStatus} />

      <ErrorBoundary resetKey={step}>
        {step === "upload" && (
          <UploadStep
            data={data}
            setData={setData}
            onNext={() => goTo("confirm")}
            onNewOnboarding={newOnboarding}
            onUploadsChanged={() => setFurthestIndex(0)}
          />
        )}
        {step === "confirm" && (
          <ConfirmReadingStep
            data={data}
            setData={setData}
            onNext={() => goTo("screening")}
            onBack={() => goTo("upload")}
          />
        )}
        {step === "screening" && (
          <ScreeningStep
            data={data}
            runner={runner}
            onNext={() => goTo("report")}
            onBack={() => goTo("confirm")}
          />
        )}
        {step === "report" && (
          <ReportStep data={data} setData={setData} onBack={() => goTo("screening")} onScreening={() => goTo("screening")} onStartOver={startOver} />
        )}
      </ErrorBoundary>
    </div>
  );
}

/* ========================================================================
 * Exported page — Keycloak auth gate wraps the wizard. See AuthGate above
 * for exactly what "just use Keycloak auth" means here: no realm-prefixed
 * links, no sidebar, no tenant display — just "is someone logged in", using
 * the same initKeycloak()/keycloak.login() Navigator itself uses.
 * ========================================================================
 */
export default function TfcDashOnboardingPage() {
  return (
    <AuthGate>
      <OnboardingWizard />
    </AuthGate>
  );
}
